import { useEffect, useState, useRef } from 'react'
import {
  FileText, FileImage, FileType, Plus, Star, Loader2, X, Trash2, Check,
  Upload, Download, AlertTriangle, BadgeCheck, ChevronDown, Pencil,
  Sparkles, ExternalLink, Hourglass, Inbox, Archive, RotateCcw, FolderDown, Stamp,
  Eye, EyeOff, Printer,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { CARD, CARD_BASE } from '../components/ui.jsx'
import { useConfirm } from '../components/ConfirmProvider.jsx'
import PdfViewer from '../components/PdfViewer.jsx'

/**
 * Documents tab body for OperationDetail.
 *
 *   Layout per kind:
 *     - For generated docs: ONE combined "สร้างเอกสาร + Approve" mini-card +
 *       ONE "ไฟล์ที่เซ็นแล้ว" mini-card. Clicking the second opens FilesDrawer.
 *     - For company-provided docs: just the "ไฟล์ที่อัปโหลด" mini-card.
 *
 *   Section grouping is driven by `document_kind.category` so new top-level
 *   sections (e.g. สสจ, ประกันสังคม) can be added by inserting kinds with a
 *   new category value — no UI changes required beyond labeling SECTIONS below.
 */
const SECTIONS = [
  { category: 'signing-prep',     title: 'เตรียมเอกสารเพื่อลงนาม', subtitle: 'สร้างจากระบบ → Approve → รอรับไฟล์ที่เซ็นกลับจาก HR' },
  { category: 'company-provided', title: 'Request เอกสารจากบริษัท', subtitle: 'HR/บริษัทอัปโหลดผ่าน HR portal — เราอัปโหลดแทนได้ในหน้าต่างไฟล์' },
  // สสจ + ประกันสังคม: every operation needs the full set, so we auto-attach
  // all kinds in these categories at op creation and hide the manual picker.
  { category: 'sso-provincial',   title: 'เอกสารสำหรับ สสจ.',         noManualAdd: true },
  { category: 'sso-security',     title: 'เอกสารสำหรับประกันสังคม',     noManualAdd: true },
  { category: 'misc',             title: 'เอกสารอื่นๆ', subtitle: 'เอกสารประจำวันและสื่อประชาสัมพันธ์บนรถ' },
]
// Anything with an unknown category falls into this catch-all section at
// the bottom so nothing silently disappears from the UI.
const FALLBACK_SECTION = { category: '__other__', title: 'อื่นๆ', subtitle: '' }

export default function OperationDocumentsTab({ op }) {
  const [requests, setRequests] = useState(null)
  const [kinds, setKinds]       = useState(null)
  const [error, setError]       = useState(null)
  const [tick, bump]            = useState(0)
  const [drawerFor, setDrawerFor] = useState(null)   // a request row when drawer open
  const [collapsed, setCollapsed] = useState(() => new Set())   // category names currently hidden

  function reload() {
    setError(null)
    Promise.all([api.operationDocuments.list(op.id), api.documentKinds.list()])
      .then(([r, k]) => { setRequests(r); setKinds(k) })
      .catch((e) => setError(e.message))
  }
  useEffect(reload, [op.id, tick])    // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorCard message={error} />
  if (!requests || !kinds) return <LoadingCard />

  const usedIds = new Set(requests.map((r) => r.kind_id))

  // Group requests + addable kinds per category in one pass. Unknown
  // categories land in __other__.
  const byCat = new Map(SECTIONS.map((s) => [s.category, { reqs: [], addable: [] }]))
  byCat.set(FALLBACK_SECTION.category, { reqs: [], addable: [] })
  for (const r of requests) {
    const key = byCat.has(r.kind_category) ? r.kind_category : FALLBACK_SECTION.category
    byCat.get(key).reqs.push(r)
  }
  for (const k of kinds) {
    // Same partial-unique-index allowance as before: the custom-company-provided
    // kind can be attached any number of times.
    const alreadyUsed = usedIds.has(k.id) && k.slug !== 'custom-company-provided'
    if (alreadyUsed) continue
    const key = byCat.has(k.category) ? k.category : FALLBACK_SECTION.category
    byCat.get(key).addable.push(k)
  }

  const sectionsToRender = [...SECTIONS, FALLBACK_SECTION].filter((s) => {
    const b = byCat.get(s.category)
    return (b.reqs.length > 0) || (b.addable.length > 0)
  })

  const drawerRow = drawerFor ? requests.find((r) => r.id === drawerFor.id) || drawerFor : null

  function toggleSection(cat) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat); else next.add(cat)
      return next
    })
  }
  function collapseAll() { setCollapsed(new Set(sectionsToRender.map((s) => s.category))) }
  function expandAll()   { setCollapsed(new Set()) }
  const anyCollapsed = sectionsToRender.some((s) => collapsed.has(s.category))
  const anyExpanded  = sectionsToRender.some((s) => !collapsed.has(s.category))

  return (
    <div className="space-y-4">
      {sectionsToRender.length > 1 && (
        <div className="flex items-center justify-end gap-1">
          <button onClick={expandAll}   disabled={!anyCollapsed}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11.5px] text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-zinc-600">
            <ChevronDown size={11} /> ขยายทั้งหมด
          </button>
          <button onClick={collapseAll} disabled={!anyExpanded}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11.5px] text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-zinc-600">
            <ChevronDown size={11} className="-rotate-90" /> ย่อทั้งหมด
          </button>
        </div>
      )}
      {sectionsToRender.map((s) => {
        const b = byCat.get(s.category)
        return (
          <DocumentSection
            key={s.category}
            category={s.category}
            title={s.title}
            subtitle={s.subtitle}
            requests={b.reqs}
            addable={s.noManualAdd ? [] : b.addable}
            hideAddPicker={!!s.noManualAdd}
            lockRows={!!s.noManualAdd}
            op={op}
            isCollapsed={collapsed.has(s.category)}
            onToggleCollapse={() => toggleSection(s.category)}
            onChange={() => bump((n) => n + 1)}
            onOpenDrawer={(r) => setDrawerFor(r)}
          />
        )
      })}

      {drawerRow && (
        <FilesDrawer
          op={op}
          request={drawerRow}
          onClose={() => setDrawerFor(null)}
          onChange={() => bump((n) => n + 1)}
        />
      )}
    </div>
  )
}


/* ──────────────────────────────── Section ──────────────────────────────── */

function DocumentSection({ category, title, subtitle, requests, addable, hideAddPicker, lockRows, op, isCollapsed, onToggleCollapse, onChange, onOpenDrawer }) {
  // Layout dispatch by category:
  //   • SSO (lockRows = true) → SsoDocumentTable, with a "ดาวน์โหลด ZIP"
  //                              shortcut in the header
  //   • company-provided      → CompanyProvidedList
  //   • everything else       → original RequestRow layout
  // Misc category shares the SSO compact-table layout, but its rows are user-managed
  // (deletable + addable), unlike the locked SSO sets.
  const useSsoList     = lockRows || category === 'misc'
  const useCompanyList = category === 'company-provided'
  const showZip        = lockRows && requests.length > 0
  return (
    <div className={`${CARD_BASE} p-5 md:p-6`}>
      <div className={'flex items-start justify-between gap-3 ' + (isCollapsed ? '' : 'mb-4')}>
        <button type="button" onClick={onToggleCollapse}
          className="min-w-0 flex items-start gap-2 text-left group">
          <ChevronDown size={14} className={
            'mt-0.5 shrink-0 text-zinc-400 group-hover:text-zinc-700 transition-transform ' +
            (isCollapsed ? '-rotate-90' : '')
          } />
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold text-zinc-900 group-hover:text-amber-700 transition-colors">{title}</h3>
            {subtitle && !isCollapsed && <p className="text-[12px] text-zinc-500 mt-0.5">{subtitle}</p>}
            {isCollapsed && <p className="text-[11.5px] text-zinc-400 mt-0.5">{requests.length} เอกสาร</p>}
          </div>
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          {showZip && <SsoZipButton opId={op.id} category={category} title={title} />}
          {!hideAddPicker && !isCollapsed && <AddDocPicker addable={addable} op={op} onChange={onChange} />}
        </div>
      </div>
      {isCollapsed ? null : requests.length === 0 ? (
        <p className="text-[12.5px] text-zinc-400 italic">ยังไม่มีเอกสารในหมวดนี้</p>
      ) : useSsoList ? (
        <SsoDocumentTable requests={requests} op={op} onChange={onChange} category={category} deletable={!lockRows} />
      ) : useCompanyList ? (
        <CompanyProvidedList requests={requests} op={op} onChange={onChange} onOpenDrawer={onOpenDrawer} />
      ) : (
        <ul className="divide-y divide-zinc-100 -mx-1.5">
          {requests.map((r) => (
            <li key={r.id} className="px-1.5 py-3 first:pt-0 last:pb-0">
              <RequestRow request={r} op={op} onChange={onChange} onOpenDrawer={onOpenDrawer} locked={lockRows} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}


function SsoZipButton({ opId, category, title, subset, variant = 'primary' }) {
  // Single-click download. Per-ref copy counts live on each reference row
  // (RefCopiesPicker) and the backend uses them automatically — no global
  // multiplier popover here.
  const [busy, setBusy] = useState(false)

  async function download() {
    if (busy) return
    setBusy(true)
    try {
      const params = new URLSearchParams({ category })
      if (subset) params.set('subset', subset)
      const r = await fetch(`/api/operations/${opId}/documents/zip?${params}`, { credentials: 'include' })
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        alert(t || 'ดาวน์โหลดไม่สำเร็จ')
        return
      }
      const blob = await r.blob()
      const cd = r.headers.get('Content-Disposition') || ''
      const m  = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
      const filename = m ? decodeURIComponent(m[1]) : `${title}.zip`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch (e) {
      alert(e?.message || 'ดาวน์โหลดไม่สำเร็จ')
    } finally { setBusy(false) }
  }

  const triggerCls = variant === 'subtle'
    ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] text-zinc-500 hover:text-amber-700 hover:bg-amber-50 normal-case tracking-normal font-normal disabled:opacity-50'
    : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 ring-1 ring-amber-300 hover:bg-amber-100 hover:ring-amber-400 text-amber-800 text-[12px] font-medium disabled:opacity-50'

  return (
    <button onClick={download} disabled={busy} title="ดาวน์โหลดเอกสารเป็น ZIP" className={triggerCls}>
      {busy
        ? <Loader2 size={variant === 'subtle' ? 10 : 11} className="animate-spin" />
        : <FolderDown size={variant === 'subtle' ? 10 : 12} />}
      ดาวน์โหลด ZIP
    </button>
  )
}


/* ──────────────────────────── SSO doc list ────────────────────────────── */
/* One responsive layout — flex row that wraps on small screens. No table,
   no separate mobile/desktop renderers; identical visuals between สสจ. and
   ประกันสังคม sections since both consume the same component. */

function SsoDocumentTable({ requests, op, onChange, category, deletable }) {
  // Split into two subgroups:
  //   • Generated docs (kind_type === 'generated') — always expanded
  //   • Reference PDFs (static_pdf_path / static_wasabi_key) — collapsed by
  //     default (staff opens when they need them) but expandable in place.
  // The reference subgroup also gets its own subtle "ดาวน์โหลด ZIP" trigger
  // with copy-count picker so staff can bundle just the reference forms.
  const generated = requests.filter((r) => r.kind_type === 'generated')
  const refs      = requests.filter((r) => r.kind_type !== 'generated' && r.kind_static_pdf_path)
  const [refsOpen, setRefsOpen] = useState(false)

  return (
    <div className="space-y-3">
      {generated.length > 0 && (
        <div>
          {refs.length > 0 && <SsoSubHeader>เอกสารที่ต้องสร้าง</SsoSubHeader>}
          <ul className="divide-y divide-zinc-100 -mx-1">
            {generated.map((r) => (
              <SsoDocumentItem key={r.id} r={r} op={op} onChange={onChange} deletable={deletable} />
            ))}
          </ul>
        </div>
      )}
      {refs.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-2 px-1 mb-1">
            <button type="button" onClick={() => setRefsOpen((v) => !v)}
              className="min-w-0 flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] font-semibold text-zinc-400 hover:text-zinc-700 transition-colors">
              <ChevronDown size={11} className={'transition-transform ' + (refsOpen ? '' : '-rotate-90')} />
              เอกสารอ้างอิง
              <span className="normal-case tracking-normal text-[10.5px] text-zinc-400 font-normal">({refs.length})</span>
            </button>
            <SsoZipButton opId={op.id} category={category} title="เอกสารอ้างอิง" subset="static" variant="subtle" />
          </div>
          {refsOpen && (
            <ul className="divide-y divide-zinc-100 -mx-1">
              {refs.map((r) => (
                <SsoDocumentItem key={r.id} r={r} op={op} onChange={onChange} deletable={deletable} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function SsoSubHeader({ children }) {
  return (
    <div className="px-1 mb-1 text-[10.5px] uppercase tracking-[0.12em] font-semibold text-zinc-400">
      {children}
    </div>
  )
}

function SsoDocumentItem({ r, op, onChange, deletable }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  const [err,  setErr]  = useState(null)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [previewing,   setPreviewing]   = useState(false)

  async function removeRequest() {
    if (!await confirm({
      title: 'ลบเอกสาร',
      message: `"${displayLabel(r)}" จะถูกลบออกจาก Operation นี้ — ไฟล์ที่สร้างไว้แล้วจะหายไปด้วย`,
      confirmLabel: 'ลบ',
      tone: 'danger',
    })) return
    setBusy(true); setErr(null)
    try { await api.operationDocuments.removeReq(op.id, r.id); onChange() }
    catch (e) { setErr(extractErr(e)); setBusy(false) }
  }

  // A kind is "static reference" when document_kind.static_pdf_path is set
  // AND there's no Apps Script template (no generation flow). These rows are
  // pure references — staff just opens or downloads them.
  const isStatic = !!r.kind_static_pdf_path && r.kind_type !== 'generated'
  const staticUrl = isStatic ? r.kind_static_pdf_path : null
  const staticFilename = staticUrl
    ? staticUrl.split('/').pop() || 'reference.pdf'
    : null

  const hasDoc = !isStatic && !!r.generated_url
  const driveId = driveIdFromUrl(r.generated_url)
  const viewUrl = r.generated_view_url || r.generated_url
  const inlineUrl = isStatic
    ? staticUrl
    : (driveId
        ? `https://drive.google.com/file/d/${driveId}/preview`
        : viewUrl)
  const downloadUrl = isStatic
    ? staticUrl
    : (driveId
        ? `https://drive.google.com/uc?export=download&id=${driveId}`
        : r.generated_url)

  async function clearGenerated() {
    if (!await confirm({
      title: 'ล้างเอกสารที่สร้างไว้',
      message: 'ลิงก์เอกสารและ approve จะถูกล้าง — ต้องสร้างใหม่ก่อน',
      confirmLabel: 'ล้าง',
      tone: 'warn',
    })) return
    setBusy(true); setErr(null)
    try {
      await api.operationDocuments.updateReq(op.id, r.id, { generated_url: null, approved: false })
      onChange()
    } catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }

  // Dot + pill color triplet — keeps amber / emerald / grey visually distinct.
  const dotCls = isStatic ? 'bg-amber-400' : hasDoc ? 'bg-emerald-500' : 'bg-zinc-300'
  const pillCls = isStatic
    ? 'bg-amber-50 text-amber-700'
    : hasDoc
      ? 'bg-emerald-50 text-emerald-700'
      : 'bg-zinc-100 text-zinc-500'
  const pillText = isStatic ? 'ไฟล์อ้างอิง' : (hasDoc ? 'สร้างแล้ว' : 'ยังไม่ได้สร้าง')

  return (
    <li className="px-2 py-3 first:pt-1 last:pb-1">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
        {/* Title side: status dot + icon + name + text pill */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span aria-hidden className={'w-1.5 h-1.5 rounded-full shrink-0 ' + dotCls} />
          <KindIcon kindSlug={r.kind_slug} size={14} />
          <span className="text-[13px] font-medium text-zinc-900 truncate">{displayLabel(r)}</span>
          <span className={'shrink-0 text-[10.5px] px-1.5 py-0.5 rounded-full font-medium ' + pillCls}>
            {pillText}
          </span>
        </div>

        {/* Actions — icon-driven, no filenames. Title row already identifies
            the doc; the actions just say what you can do with it. */}
        <div className="flex items-center gap-1 flex-wrap sm:flex-nowrap sm:shrink-0">
          {isStatic ? (
            <>
              <RefCopiesPicker opId={op.id} r={r} onChange={onChange} />
              <button onClick={() => setPreviewing(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 hover:bg-zinc-50 text-zinc-700 text-[12px]">
                <Eye size={11} /> เปิดดู
              </button>
              <a href={staticUrl} download
                title="ดาวน์โหลด"
                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-amber-700 hover:bg-amber-50">
                <Download size={12} />
              </a>
            </>
          ) : hasDoc ? (
            <>
              <button onClick={() => setPreviewing(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 hover:bg-zinc-50 text-zinc-700 text-[12px]">
                <Eye size={11} /> เปิดดู
              </button>
              <button onClick={() => setGenerateOpen(true)} disabled={busy}
                title="สร้างใหม่"
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-[12px] text-zinc-500 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                <Sparkles size={11} /> สร้างใหม่
              </button>
              <button onClick={clearGenerated} disabled={busy}
                title="ล้างเอกสารที่สร้างไว้"
                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                <RotateCcw size={12} />
              </button>
            </>
          ) : (
            <button onClick={() => setGenerateOpen(true)} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60 shadow-sm shadow-amber-500/20">
              <Sparkles size={11} /> สร้างเอกสาร
            </button>
          )}
          {deletable && (
            <button onClick={removeRequest} disabled={busy} title="ลบเอกสารนี้จาก Operation"
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50">
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      {err && (
        <div className="mt-2 ml-3.5 px-2 py-1 rounded bg-rose-50 text-rose-700 text-[11px] inline-flex items-center gap-1.5">
          <AlertTriangle size={10} /> {err}
        </div>
      )}

      {generateOpen && !isStatic && (
        <GenerateModal r={r} op={op}
          onClose={() => setGenerateOpen(false)}
          onGenerated={() => { setGenerateOpen(false); onChange() }} />
      )}
      {previewing && (
        <FilePreviewModal
          filename={isStatic ? staticFilename : (r.generated_filename || 'document.pdf')}
          contentType="application/pdf"
          inlineUrl={inlineUrl}
          viewUrl={isStatic ? staticUrl : viewUrl}
          downloadUrl={downloadUrl}
          onClose={() => setPreviewing(false)}
        />
      )}
    </li>
  )
}


/**
 * Per-reference print-copy picker. Single pill button shows the current
 * count (e.g. "2 ชุด"); click to open a small dropdown with 1/2/3 options.
 * Persists to document_request.ref_copies via PATCH with optimistic UI.
 */
function RefCopiesPicker({ opId, r, onChange }) {
  const [val,  setVal]  = useState(Math.max(1, Math.min(3, parseInt(r.ref_copies, 10) || 1)))
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onEsc   = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  async function pick(n) {
    setOpen(false)
    if (busy || n === val) return
    const prev = val
    setVal(n); setBusy(true)
    try {
      await api.operationDocuments.updateReq(opId, r.id, { ref_copies: n })
      onChange()
    } catch {
      setVal(prev)
    } finally { setBusy(false) }
  }

  // Highlight when more than 1 — quick visual cue that staff has bumped this up.
  const accent = val > 1 ? 'bg-amber-50 ring-amber-300 text-amber-800' : 'ring-zinc-200 text-zinc-600 hover:bg-zinc-50'

  return (
    <div ref={ref} className="relative inline-block" title="จำนวนสำเนาเมื่อดาวน์โหลด ZIP">
      <button type="button" onClick={() => setOpen((v) => !v)} disabled={busy}
        className={
          'inline-flex items-center gap-1 px-2 py-1.5 rounded-md ring-1 text-[12px] tabular-nums font-medium disabled:opacity-50 ' +
          accent
        }>
        {val} ชุด
        <ChevronDown size={10} className={'transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-[120px] rounded-lg bg-white ring-1 ring-zinc-200 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)] p-1">
          <div className="px-2 pt-1 pb-1.5 text-[10.5px] uppercase tracking-wider font-semibold text-zinc-400">
            จำนวนสำเนา
          </div>
          {[1, 2, 3].map((n) => (
            <button key={n} type="button" onClick={() => pick(n)}
              className={
                'w-full flex items-center justify-between px-2 py-1.5 rounded text-[12.5px] tabular-nums ' +
                (n === val ? 'bg-amber-50 text-amber-800 font-medium' : 'text-zinc-700 hover:bg-zinc-50')
              }>
              <span>{n} ชุด</span>
              {n === val && <Check size={11} className="text-amber-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}


/* ───────────────────────── Company-provided doc list ──────────────────── */
/* Mirrors the SSO list visuals (status dot + icon + name + pill + actions)
   but adapted to the company-provided workflow: status reflects whether HR
   has uploaded files yet, the primary action opens the file-management
   drawer, and per-row edit/delete + note editing are preserved (these rows
   are NOT locked). */

function CompanyProvidedList({ requests, op, onChange, onOpenDrawer }) {
  return (
    <ul className="divide-y divide-zinc-100 -mx-1">
      {requests.map((r) => (
        <CompanyProvidedItem key={r.id} r={r} op={op} onChange={onChange} onOpenDrawer={onOpenDrawer} />
      ))}
    </ul>
  )
}

function CompanyProvidedItem({ r, op, onChange, onOpenDrawer }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  const [err,  setErr]  = useState(null)
  const [showNote, setShowNote] = useState(false)
  const [noteDraft, setNoteDraft] = useState(r.note || '')

  const liveFiles     = r.files          || []
  const inactiveFiles = r.deleted_files  || []
  const hasFiles      = liveFiles.length > 0

  async function removeRequest() {
    if (!await confirm({
      title: 'ลบเอกสารที่ขอจากบริษัท',
      message: `เอกสาร "${displayLabel(r)}" จะถูกลบออกจาก Operation นี้ — ไฟล์ที่อัปโหลดไว้แล้วก็จะหายไปด้วย`,
      confirmLabel: 'ลบ',
      tone: 'danger',
    })) return
    setBusy(true); setErr(null)
    try { await api.operationDocuments.removeReq(op.id, r.id); onChange() }
    catch (e) { setErr(extractErr(e)); setBusy(false) }
  }
  async function saveNote() {
    setBusy(true); setErr(null)
    try { await api.operationDocuments.updateReq(op.id, r.id, { note: noteDraft }); onChange(); setShowNote(false) }
    catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }

  return (
    <li className="px-2 py-3 first:pt-1 last:pb-1 group">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
        {/* Title side */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            aria-hidden
            className={
              'w-1.5 h-1.5 rounded-full shrink-0 ' +
              (hasFiles ? 'bg-emerald-500' : 'bg-zinc-300')
            }
          />
          <KindIcon kindSlug={r.kind_slug} size={14} />
          <span className="text-[13px] font-medium text-zinc-900 truncate">{displayLabel(r)}</span>
          <span className={
            'shrink-0 text-[10.5px] px-1.5 py-0.5 rounded-full font-medium ' +
            (hasFiles ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500')
          }>
            {hasFiles ? `${liveFiles.length} ไฟล์` : 'ยังไม่มีไฟล์'}
          </span>
        </div>

        {/* Actions: file management button + per-row edit/delete */}
        <div className="flex items-center gap-1 flex-wrap sm:flex-nowrap sm:shrink-0">
          <button
            onClick={() => onOpenDrawer(r)}
            className={
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium ' +
              (hasFiles
                ? 'text-emerald-700 hover:bg-emerald-50'
                : 'ring-1 ring-amber-300 text-amber-800 hover:bg-amber-50')
            }
          >
            <Inbox size={11} />
            {hasFiles ? 'จัดการไฟล์' : 'อัปโหลด / จัดการไฟล์'}
            {inactiveFiles.length > 0 && (
              <span className="text-[10.5px] text-zinc-400 ml-1">+{inactiveFiles.length} inactive</span>
            )}
          </button>
          <button onClick={() => setShowNote((s) => !s)} title="แก้ไขโน้ต"
            className="w-8 h-8 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 inline-flex items-center justify-center opacity-60 group-hover:opacity-100 transition">
            <Pencil size={11} />
          </button>
          <button onClick={removeRequest} disabled={busy} title="ลบเอกสารนี้จาก Operation"
            className="w-8 h-8 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center disabled:opacity-50 opacity-60 group-hover:opacity-100 transition">
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      {r.note && !showNote && (
        <p className="mt-1 ml-3.5 text-[12px] text-zinc-500 italic">{r.note}</p>
      )}
      {showNote && (
        <div className="mt-2 ml-3.5 flex items-center gap-2">
          <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="โน้ตเพิ่มเติม" maxLength={1000}
            className="flex-1 px-2 py-1 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[12.5px]" />
          <button onClick={saveNote} disabled={busy}
            className="px-2.5 py-1 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-medium disabled:opacity-60">บันทึก</button>
          <button onClick={() => { setShowNote(false); setNoteDraft(r.note || '') }}
            className="text-[12px] text-zinc-500 hover:text-zinc-900 px-1">ยกเลิก</button>
        </div>
      )}

      {err && (
        <div className="mt-2 ml-3.5 px-2 py-1 rounded bg-rose-50 text-rose-700 text-[11px] inline-flex items-center gap-1.5">
          <AlertTriangle size={10} /> {err}
        </div>
      )}
    </li>
  )
}


/* ─────────── Inline "add" picker (per section header) ─────────── */

function AddDocPicker({ addable, op, onChange }) {
  const [open, setOpen] = useState(false)
  const [busyId, setBusyId] = useState(null)   // null | kind.id during add OR 'fav-<id>' during favorite toggle
  const [customForId, setCustomForId] = useState(null)
  const [customLabel, setCustomLabel] = useState('')
  const [err, setErr] = useState(null)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setCustomForId(null) } }
    const onEsc   = (e) => { if (e.key === 'Escape')   { setOpen(false); setCustomForId(null) } }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  async function addStandard(k) {
    setBusyId(k.id); setErr(null)
    try {
      await api.operationDocuments.addRequest(op.id, { kind_id: k.id })
      setOpen(false); onChange()
    } catch (e) { setErr(extractErr(e)) }
    finally   { setBusyId(null) }
  }
  async function addCustom() {
    if (!customLabel.trim()) { setErr('กรุณาใส่ชื่อเอกสาร'); return }
    setBusyId(customForId); setErr(null)
    try {
      await api.operationDocuments.addRequest(op.id, { kind_id: customForId, custom_label: customLabel.trim() })
      setOpen(false); setCustomForId(null); setCustomLabel(''); onChange()
    } catch (e) { setErr(extractErr(e)) }
    finally   { setBusyId(null) }
  }
  async function toggleFav(k, e) {
    e.stopPropagation()
    setBusyId(`fav-${k.id}`); setErr(null)
    try {
      if (k.is_favorite) await api.documentKinds.unfavorite(k.id)
      else               await api.documentKinds.favorite(k.id)
      onChange()
    } finally { setBusyId(null) }
  }

  return (
    <div ref={ref} className="relative shrink-0">
      <button onClick={() => { setOpen((o) => !o); setCustomForId(null); setErr(null) }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md ring-1 ring-amber-300 hover:bg-amber-50 text-amber-800 text-[12.5px] font-medium">
        <Plus size={12} /> เพิ่มเอกสาร
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-20 w-[320px] bg-white rounded-xl ring-1 ring-zinc-200 shadow-[0_10px_32px_-8px_rgba(0,0,0,0.18)] p-2">
          {addable.length === 0 ? (
            <div className="px-2 py-3 text-[12px] text-zinc-400 text-center italic">
              เอกสารทั้งหมดในหมวดนี้ถูกขอแล้ว
            </div>
          ) : (
            <ul className="space-y-0.5 max-h-[320px] overflow-y-auto">
              {addable.map((k) => {
                const isCustomKind = k.slug === 'custom-company-provided'
                const isOpenForCustom = customForId === k.id
                if (isOpenForCustom) {
                  return (
                    <li key={k.id} className="px-1 py-1.5">
                      <p className="text-[10.5px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">ขอเอกสารแบบกำหนดเอง</p>
                      <input value={customLabel} autoFocus maxLength={200}
                        onChange={(e) => setCustomLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addCustom() }}
                        placeholder="เช่น สำเนาใบสำคัญผู้ถือหุ้น"
                        className="w-full px-2 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[13px]" />
                      <div className="mt-1.5 flex items-center justify-end gap-2">
                        <button onClick={() => { setCustomForId(null); setCustomLabel(''); setErr(null) }}
                          className="text-[11.5px] text-zinc-500 hover:text-zinc-900 px-1.5 py-0.5">ยกเลิก</button>
                        <button onClick={addCustom} disabled={busyId === k.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[11.5px] font-medium disabled:opacity-60">
                          {busyId === k.id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                          เพิ่ม
                        </button>
                      </div>
                    </li>
                  )
                }
                return (
                  <li key={k.id} className="flex items-center gap-1">
                    <button
                      onClick={() => isCustomKind ? setCustomForId(k.id) : addStandard(k)}
                      disabled={busyId === k.id}
                      className="flex-1 text-left flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-amber-50 text-[12.5px] disabled:opacity-60">
                      <KindIcon kindSlug={k.slug} size={14} />
                      <span className="truncate flex-1">
                        {isCustomKind ? <span className="text-amber-800 font-medium">+ {k.name}…</span> : k.name}
                      </span>
                      {busyId === k.id && <Loader2 size={11} className="animate-spin text-zinc-400" />}
                    </button>
                    {/* Favorite toggle — disabled for the custom kind (doesn't make
                        sense to auto-add unnamed customs on new ops) */}
                    {!isCustomKind && (
                      <button onClick={(e) => toggleFav(k, e)} disabled={busyId === `fav-${k.id}`}
                        title={k.is_favorite ? 'เลิกเป็น Favorite' : 'ติด Favorite — auto-add ทุก Operation ใหม่ที่คุณสร้าง'}
                        className="w-7 h-7 rounded-md inline-flex items-center justify-center hover:bg-zinc-100 disabled:opacity-50">
                        <Star size={12} className={k.is_favorite ? 'text-amber-500 fill-amber-500' : 'text-zinc-300'} />
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          {err && (
            <div className="mt-2 px-2 py-1.5 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[11.5px]">{err}</div>
          )}
        </div>
      )}
    </div>
  )
}


/* ──────────────────────────────── Request row ──────────────────────────── */

function RequestRow({ request: r, op, onChange, onOpenDrawer, locked }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const [showNote, setShowNote] = useState(false)
  const [noteDraft, setNoteDraft] = useState(r.note || '')
  const [generateOpen, setGenerateOpen] = useState(false)

  const isGenerated = r.kind_type === 'generated'
  const liveFiles    = r.files         || []
  const inactiveFiles = r.deleted_files || []

  async function toggleApproved() {
    setBusy(true); setErr(null)
    try { await api.operationDocuments.updateReq(op.id, r.id, { approved: !r.approved }); onChange() }
    catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }
  async function removeRequest() {
    if (!await confirm({
      title: 'ลบเอกสารที่ขอจากบริษัท',
      message: `เอกสาร "${displayLabel(r)}" จะถูกลบออกจาก Operation นี้ — ไฟล์ที่อัปโหลดไว้แล้วก็จะหายไปด้วย`,
      confirmLabel: 'ลบ',
      tone: 'danger',
    })) return
    setBusy(true); setErr(null)
    try { await api.operationDocuments.removeReq(op.id, r.id); onChange() }
    catch (e) { setErr(extractErr(e)); setBusy(false) }
  }
  async function saveNote() {
    setBusy(true); setErr(null)
    try { await api.operationDocuments.updateReq(op.id, r.id, { note: noteDraft }); onChange(); setShowNote(false) }
    catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }
  async function clearGenerated() {
    if (!await confirm({
      title: 'ล้างเอกสารที่สร้างไว้',
      message: 'ลิงก์เอกสารและ approve จะถูกล้าง — ต้องสร้างใหม่ก่อน',
      confirmLabel: 'ล้าง',
      tone: 'warn',
    })) return
    setBusy(true); setErr(null)
    try {
      await api.operationDocuments.updateReq(op.id, r.id, { generated_url: null, approved: false })
      onChange()
    } catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }

  const hasDoc = !!r.generated_url
  const approved = !!r.approved
  const hasFiles = liveFiles.length > 0
  const waitingFiles = isGenerated && approved && !hasFiles
  // Static reference PDFs (e.g. blank gov't forms) — we don't generate or
  // collect files; just show a download link so staff has everything in one
  // place. Identified by document_kind.static_pdf_path being set.
  const isStatic = !!r.kind_static_pdf_path
  // SSO sections use a compact single-row layout — no left/right cards, no
  // signed-copies workflow. Just generate + click to preview.
  const isSsoSection = r.kind_category === 'sso-provincial' || r.kind_category === 'sso-security'

  return (
    <div className="group">
      {/* Title line */}
      <div className="flex items-start gap-3">
        <KindIcon kindSlug={r.kind_slug} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13.5px] font-semibold text-zinc-900">{displayLabel(r)}</span>
            {isStatic ? <StaticPdfChip />
              : isSsoSection && isGenerated ? (hasDoc ? <SsoGeneratedChip /> : null)
              : isGenerated ? <ProgressDots done={[hasDoc, approved, hasFiles]} />
              : <FileCountChip count={liveFiles.length} />}
          </div>
          {r.note && !showNote && (
            <p className="mt-0.5 text-[12px] text-zinc-500 italic">{r.note}</p>
          )}
          {showNote && !locked && (
            <div className="mt-2 flex items-center gap-2">
              <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="โน้ตเพิ่มเติม"
                maxLength={1000}
                className="flex-1 px-2 py-1 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[12.5px]" />
              <button onClick={saveNote} disabled={busy}
                className="px-2.5 py-1 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-medium disabled:opacity-60">บันทึก</button>
              <button onClick={() => { setShowNote(false); setNoteDraft(r.note || '') }}
                className="text-[12px] text-zinc-500 hover:text-zinc-900 px-1">ยกเลิก</button>
            </div>
          )}
        </div>
        {!locked && (
          <div className="shrink-0 flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition">
            <button onClick={() => setShowNote((s) => !s)} title="แก้ไขโน้ต"
              className="w-7 h-7 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 inline-flex items-center justify-center">
              <Pencil size={12} />
            </button>
            <button onClick={removeRequest} disabled={busy} title="ลบเอกสารนี้จาก Operation"
              className="w-7 h-7 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center disabled:opacity-50">
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Body — compact row for SSO sections, 2-column cards for signing-prep,
          single inline action row for provided docs, download link for static. */}
      {isStatic ? (
        <div className="mt-2 ml-[26px]">
          <a href={r.kind_static_pdf_path} target="_blank" rel="noreferrer"
            download
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ring-1 ring-amber-300 hover:bg-amber-50 text-amber-800 text-[12.5px] font-medium">
            <Download size={11} /> ดาวน์โหลด PDF
          </a>
        </div>
      ) : isSsoSection && isGenerated ? (
        <SsoGeneratedRow r={r} busy={busy}
          onGenerate={() => setGenerateOpen(true)}
          onClear={clearGenerated} />
      ) : isGenerated ? (
        <div className="mt-2.5 ml-[26px] grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <LeftDocCard r={r} busy={busy}
            onGenerate={() => setGenerateOpen(true)}
            onClear={clearGenerated}
            onToggleApproved={toggleApproved} />
          <RightFilesCard r={r} op={op} liveFiles={liveFiles} inactiveFiles={inactiveFiles}
            onOpen={() => onOpenDrawer(r)} onChange={onChange} />
        </div>
      ) : (
        <div className="mt-2 ml-[26px] flex items-center gap-1 flex-wrap text-[12px]">
          <button onClick={() => onOpenDrawer(r)}
            className={'inline-flex items-center gap-1 px-2 py-1 rounded-md font-medium ' +
              (hasFiles
                ? 'text-emerald-700 hover:bg-emerald-50'
                : 'text-zinc-600 hover:bg-zinc-100')}>
            <Inbox size={11} />
            {hasFiles ? `จัดการไฟล์ (${liveFiles.length})` : 'อัปโหลด / จัดการไฟล์'}
          </button>
          {inactiveFiles.length > 0 && (
            <span className="text-[10.5px] text-zinc-400 ml-1">+{inactiveFiles.length} inactive</span>
          )}
        </div>
      )}

      {err && (
        <div className="mt-2 ml-[26px] px-2.5 py-1 rounded-md bg-rose-50 text-rose-700 text-[11.5px] inline-flex items-center gap-1.5">
          <AlertTriangle size={11} /> {err}
        </div>
      )}

      {generateOpen && (
        <GenerateModal
          r={r}
          op={op}
          onClose={() => setGenerateOpen(false)}
          onGenerated={() => { setGenerateOpen(false); onChange() }}
        />
      )}
    </div>
  )
}

function ProgressDots({ done }) {
  const labels = ['สร้าง', 'Approve', 'ไฟล์']
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] text-zinc-500">
      {done.map((d, i) => (
        <span key={i} className="inline-flex items-center gap-0.5">
          <span className={'w-1.5 h-1.5 rounded-full ' +
            (d ? 'bg-emerald-500' : 'bg-zinc-300')} />
          <span className={d ? 'text-emerald-700' : 'text-zinc-400'}>{labels[i]}</span>
        </span>
      ))}
    </span>
  )
}

function FileCountChip({ count }) {
  return (
    <span className={'text-[10.5px] px-1.5 py-0.5 rounded font-medium ' +
      (count > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500')}>
      {count > 0 ? `${count} ไฟล์` : 'ยังไม่มีไฟล์'}
    </span>
  )
}

function StaticPdfChip() {
  return (
    <span className="text-[10.5px] px-1.5 py-0.5 rounded font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
      ไฟล์อ้างอิง
    </span>
  )
}

function SsoGeneratedChip() {
  return (
    <span className="text-[10.5px] px-1.5 py-0.5 rounded font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
      สร้างแล้ว
    </span>
  )
}

/** Compact body row used by sso-provincial / sso-security kinds.
 *  No left/right card split, no HR signed-copies workflow.
 *  Just: generate button (empty state) OR filename + preview + regenerate. */
function SsoGeneratedRow({ r, busy, onGenerate, onClear }) {
  const [previewing, setPreviewing] = useState(false)
  const hasDoc = !!r.generated_url
  const driveId = driveIdFromUrl(r.generated_url)
  // generated_view_url = presigned without Content-Disposition (set by GET endpoint).
  // Fall back to generated_url for Drive links (they open inline by default).
  const viewUrl = r.generated_view_url || r.generated_url
  const inlineUrl = driveId
    ? `https://drive.google.com/file/d/${driveId}/preview`
    : viewUrl
  const downloadUrl = driveId
    ? `https://drive.google.com/uc?export=download&id=${driveId}`
    : r.generated_url  // original with Content-Disposition for the download button

  if (!hasDoc) {
    return (
      <div className="mt-2 ml-[26px]">
        <button onClick={onGenerate} disabled={busy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ring-1 ring-amber-300 hover:bg-amber-50 text-amber-800 text-[12.5px] font-medium disabled:opacity-60">
          <Sparkles size={11} /> สร้างเอกสาร
        </button>
      </div>
    )
  }

  return (
    <div className="mt-2 ml-[26px] flex items-center gap-1.5 flex-wrap">
      <button onClick={() => setPreviewing(true)}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[12.5px] font-medium text-zinc-800 hover:bg-zinc-100 max-w-[320px]">
        <FileType size={12} className="text-rose-400 shrink-0" />
        <span className="truncate">{r.generated_filename || 'เอกสาร'}</span>
      </button>
      <span className="w-px h-3.5 bg-zinc-200" />
      <button onClick={onGenerate} disabled={busy}
        title="สร้างใหม่"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11.5px] text-zinc-400 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-50">
        <Sparkles size={10} /> สร้างใหม่
      </button>
      {previewing && (
        <FilePreviewModal
          filename={r.generated_filename || 'document.pdf'}
          contentType="application/pdf"
          inlineUrl={inlineUrl}
          viewUrl={viewUrl}
          downloadUrl={downloadUrl}
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  )
}


/* ──────────────────────────────── 2-column doc cards ──────────────────── */

const CARD_BASE_CLS    = 'rounded-xl ring-1 ring-zinc-200/70 bg-white overflow-hidden flex flex-col'
const CARD_LABEL_CLS   = 'text-[10px] uppercase tracking-[0.14em] font-semibold text-zinc-500 px-3 py-1.5 bg-zinc-50/60 border-b border-zinc-100'

/** LEFT — our side: generate, view, approve, regenerate. */
function LeftDocCard({ r, busy, onGenerate, onClear, onToggleApproved }) {
  const hasDoc   = !!r.generated_url
  const approved = !!r.approved
  const driveId  = driveIdFromUrl(r.generated_url)
  // View URL has no Content-Disposition so browsers open inline, not download.
  const viewUrl  = r.generated_view_url || r.generated_url
  const [previewing, setPreviewing] = useState(false)

  return (
    <div className={CARD_BASE_CLS}>
      <div className={CARD_LABEL_CLS}>ฝั่งของเรา</div>

      {hasDoc ? (
        <div className="px-3 py-2.5 flex-1 flex flex-col gap-2">
          <div className="flex items-start gap-3">
            <button type="button" onClick={() => setPreviewing(true)}
              title="เปิดดูเอกสาร"
              className="shrink-0 w-16 h-16 rounded-md bg-zinc-100 ring-1 ring-zinc-200 overflow-hidden inline-flex items-center justify-center text-zinc-300 hover:ring-amber-300 transition">
              <DriveThumb id={driveId} />
            </button>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] text-zinc-900 truncate font-medium" title={r.generated_filename}>
                {r.generated_filename || '—'}
              </div>
              <div className="mt-0.5 text-[10.5px] text-zinc-500 tabular-nums leading-snug">
                สร้าง {fmtDate(r.generated_at)}
              </div>
              {approved && r.approved_at && (
                <div className="text-[10.5px] text-emerald-600 tabular-nums leading-snug">
                  Approved {fmtDate(r.approved_at)}
                </div>
              )}
            </div>
          </div>
          <div className="mt-auto pt-1 flex items-center gap-1.5 flex-wrap">
            <a href={r.generated_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium text-zinc-700 hover:bg-zinc-100">
              <ExternalLink size={11} /> ดูเอกสาร
            </a>
            {approved ? (
              // Done state — soft emerald pill, reads as STATUS.
              <button onClick={onToggleApproved} disabled={busy}
                title="คลิกเพื่อยกเลิก Approve"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11.5px] font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 hover:ring-emerald-300 hover:bg-emerald-100">
                <BadgeCheck size={11} /> Approved
              </button>
            ) : (
              // Pending state — plain outlined button. White bg, grey border, no fill.
              <button onClick={onToggleApproved} disabled={busy}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-zinc-700 bg-white ring-1 ring-zinc-300 hover:ring-zinc-400 hover:bg-zinc-50 disabled:opacity-60">
                <Stamp size={11} /> Approve
              </button>
            )}
            <button onClick={onGenerate} disabled={busy}
              title="สร้างเอกสารใหม่ (จะล้าง approval)"
              className="inline-flex items-center gap-1 px-1.5 py-1 rounded-md text-[11.5px] text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100">
              <Sparkles size={10} /> สร้างใหม่
            </button>
            <button onClick={onClear} disabled={busy}
              title="ล้างเอกสารที่สร้างไว้"
              className="ml-auto inline-flex items-center justify-center w-6 h-6 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50">
              <Trash2 size={11} />
            </button>
          </div>
        </div>
      ) : (
        <button onClick={onGenerate} disabled={busy}
          className="flex-1 px-4 py-6 flex flex-col items-center justify-center gap-2 text-center bg-gradient-to-br from-amber-50/60 to-orange-50/40 hover:from-amber-50 hover:to-orange-50 transition disabled:opacity-60">
          <div className="w-11 h-11 rounded-full bg-amber-500 text-white inline-flex items-center justify-center shadow-sm">
            <Sparkles size={18} />
          </div>
          <span className="text-[13px] font-semibold text-zinc-900">สร้างเอกสาร</span>
          <span className="text-[11px] text-zinc-500 leading-snug max-w-[180px]">กรอกข้อมูลสัญญา แล้วระบบจะสร้างไฟล์ PDF ให้</span>
        </button>
      )}
      {previewing && (
        <FilePreviewModal
          filename={r.generated_filename || 'document.pdf'}
          contentType="application/pdf"
          inlineUrl={driveId ? `https://drive.google.com/file/d/${driveId}/preview` : viewUrl}
          viewUrl={viewUrl}
          downloadUrl={driveId ? `https://drive.google.com/uc?export=download&id=${driveId}` : r.generated_url}
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  )
}

// 64×64 Drive thumbnail with a clean PDF-icon fallback. Drive's thumbnail
// endpoint sometimes returns 404 / a placeholder for "Anyone with the link"
// files; we fall back to a stylized icon so the card never breaks.
function DriveThumb({ id }) {
  const [errored, setErrored] = useState(false)
  if (!id || errored) {
    return <FileType size={24} className="text-rose-400" />
  }
  return (
    <img src={`https://drive.google.com/thumbnail?id=${id}&sz=w128`}
      alt="" loading="lazy" referrerPolicy="no-referrer"
      className="w-full h-full object-cover"
      onError={() => setErrored(true)} />
  )
}

/** RIGHT — HR / company's side: signed files received back. */
function RightFilesCard({ r, op, liveFiles, inactiveFiles, onOpen, onChange }) {
  const hasDoc   = !!r.generated_url
  const hasFiles = liveFiles.length > 0
  const [zipping, setZipping] = useState(false)
  const [zipErr, setZipErr]   = useState(null)

  async function downloadZip() {
    if (zipping) return
    setZipErr(null); setZipping(true)
    try { await downloadFilesAsZip(op, r, liveFiles) }
    catch (e) { setZipErr(extractErr(e)) }
    finally   { setZipping(false) }
  }

  return (
    <div className={CARD_BASE_CLS}>
      <div className={CARD_LABEL_CLS}>ไฟล์ที่ HR / บริษัทส่งกลับ</div>

      {!hasDoc ? (
        <div className="flex-1 px-4 py-6 flex flex-col items-center justify-center gap-2 text-center bg-zinc-50/60 text-zinc-400">
          <Inbox size={22} />
          <span className="text-[12px] leading-snug max-w-[200px]">สร้างเอกสารด้านซ้ายก่อน — HR ถึงจะอัปโหลดไฟล์ที่เซ็นกลับมาได้</span>
        </div>
      ) : hasFiles ? (
        <div className="px-3 py-2.5 flex-1 flex flex-col gap-2">
          {/* Bigger 64×80 thumbnails — each tile is a clickable link that opens the file in a new tab. */}
          <div className="flex items-start gap-2.5">
            <div className="shrink-0 flex items-start gap-1.5">
              {liveFiles.slice(0, 3).map((f) => <FileThumbLink key={f.id} f={f} op={op} request={r} onChanged={onChange} />)}
              {liveFiles.length > 3 && (
                <div className="w-16 h-20 rounded-md bg-zinc-100 ring-1 ring-zinc-200 inline-flex items-center justify-center text-[12px] font-semibold text-zinc-600">
                  +{liveFiles.length - 3}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] text-zinc-900 font-medium">
                {liveFiles.length} ไฟล์ที่ได้รับ
                {inactiveFiles.length > 0 && (
                  <span className="ml-1.5 text-[10.5px] text-zinc-400 font-normal">+{inactiveFiles.length} inactive</span>
                )}
              </div>
              <div className="mt-0.5 text-[10.5px] text-zinc-500 tabular-nums leading-snug">
                ล่าสุด {fmtDate(liveFiles[0]?.uploaded_at)}
              </div>
            </div>
          </div>
          {zipErr && (
            <div className="text-[11px] text-rose-600 inline-flex items-center gap-1">
              <AlertTriangle size={10} /> {zipErr}
            </div>
          )}
          <div className="mt-auto pt-1 flex items-center gap-1.5 flex-wrap">
            <button onClick={onOpen}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium text-emerald-700 hover:bg-emerald-50">
              <FolderDown size={11} /> จัดการไฟล์
            </button>
            <button onClick={downloadZip} disabled={zipping}
              title="ดาวน์โหลดไฟล์ทั้งหมดเป็น .zip"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium text-zinc-600 hover:bg-zinc-100 disabled:opacity-60">
              {zipping ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
              ดาวน์โหลด ZIP
            </button>
          </div>
        </div>
      ) : (
        <button onClick={onOpen}
          className="flex-1 px-4 py-6 flex flex-col items-center justify-center gap-2 text-center bg-zinc-50/40 hover:bg-zinc-50 transition">
          <Inbox size={22} className="text-zinc-300" />
          <span className="text-[12.5px] font-medium text-zinc-700">ยังไม่ได้รับไฟล์</span>
          <span className="text-[11px] text-zinc-500 leading-snug max-w-[200px]">รอ HR ส่งไฟล์ที่เซ็นกลับ — หรือคลิกเพื่ออัปโหลดแทน</span>
        </button>
      )}
    </div>
  )
}

function FileThumb({ f }) {
  // Compact 48×48 variant used by … (currently nothing — kept for callers that
  // want a tiny preview). The richer clickable version is FileThumbLink below.
  const [imgFailed, setImgFailed] = useState(false)
  const isImage = typeof f.content_type === 'string' && f.content_type.startsWith('image/')
  const isPdf   = f.content_type === 'application/pdf'

  if (isImage && f.url && !imgFailed) {
    return (
      <img src={f.url} alt={f.filename} loading="lazy"
        className="w-full h-full object-cover"
        title={f.filename}
        onError={() => setImgFailed(true)} />
    )
  }
  if (isPdf) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-rose-50 to-rose-100 flex flex-col items-center justify-center text-rose-600" title={f.filename}>
        <FileType size={18} />
        <span className="text-[9px] font-bold mt-0.5 tracking-wider">PDF</span>
      </div>
    )
  }
  return (
    <div className="w-full h-full bg-zinc-100 flex flex-col items-center justify-center text-zinc-500" title={f.filename}>
      <FileText size={18} />
      <span className="text-[9px] font-bold mt-0.5 tracking-wider">FILE</span>
    </div>
  )
}

// Larger clickable thumbnail (64×80 page ratio). Click opens an in-app
// preview modal (FilePreviewModal below) — images render inline, PDFs in an
// iframe, with download + open-in-new-tab fallbacks. Tile itself renders
// the actual image preview for images or a styled icon card otherwise.
function FileThumbLink({ f, op, request, onChanged }) {
  const [imgFailed, setImgFailed] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const isImage = typeof f.content_type === 'string' && f.content_type.startsWith('image/')
  const isPdf   = f.content_type === 'application/pdf'

  // Same-origin proxy URL — used by the modal's iframe so Arc Shields /
  // X-Frame-Options / ad-blockers don't block the cross-origin Wasabi load.
  const proxyUrl = `/api/operations/${op.id}/documents/${request.id}/files/${f.id}/preview`

  let preview
  if (isImage && f.url && !imgFailed) {
    preview = (
      <img src={f.url} alt={f.filename} loading="lazy"
        className="w-full h-full object-cover pointer-events-none"
        onError={() => setImgFailed(true)} />
    )
  } else if (isPdf) {
    preview = (
      <div className="w-full h-full bg-gradient-to-br from-amber-50 to-orange-100 flex flex-col items-center justify-center text-amber-700">
        <FileType size={22} />
        <span className="text-[9px] font-bold mt-0.5 tracking-wider">PDF</span>
      </div>
    )
  } else {
    preview = (
      <div className="w-full h-full bg-gradient-to-br from-amber-50 to-orange-100 flex flex-col items-center justify-center text-amber-700">
        <FileText size={22} />
        <span className="text-[9px] font-bold mt-0.5 tracking-wider">FILE</span>
      </div>
    )
  }

  return (
    <>
      <button type="button" onClick={() => setPreviewing(true)}
        title={f.filename}
        className="block w-16 h-20 rounded-md bg-zinc-100 ring-1 ring-zinc-200 hover:ring-amber-400 overflow-hidden relative group transition">
        {preview}
        <span className="absolute inset-x-0 bottom-0 px-1 py-0.5 bg-zinc-900/60 text-white text-[9px] truncate text-center opacity-0 group-hover:opacity-100 transition">
          {f.filename}
        </span>
      </button>
      {previewing && (
        <FilePreviewModal
          file={f} op={op} request={request}
          filename={f.filename}
          contentType={f.content_type}
          inlineUrl={proxyUrl}
          externalUrl={f.url}
          downloadUrl={f.download_url || f.url}
          onClose={() => setPreviewing(false)}
          onChanged={onChanged}
        />
      )}
    </>
  )
}

// Generic fullscreen preview modal. The caller passes:
//   inlineUrl    — what the iframe / img uses (rendered inline; presigned OR
//                  a same-origin proxy URL to dodge cross-origin iframe blockers)
//   viewUrl      — optional; used for "เปิดในแท็บใหม่". Falls back to inlineUrl.
//                  Pass a presigned URL without Content-Disposition for Wasabi PDFs
//                  so the browser opens them inline instead of downloading.
//   downloadUrl  — used by the download link (Content-Disposition: attachment)
//   file/op/request/onChanged (optional) — when present, the modal also shows
//                  the inactivate / delete actions for that file
//
// Used for Wasabi-hosted right-card files AND the Drive-hosted generated MoU
// on the left (in which case inlineUrl is drive.com/file/<id>/preview and
// the file-action props are omitted).
function FilePreviewModal({ filename, contentType, inlineUrl, viewUrl, downloadUrl, onClose, file, op, request, onChanged }) {
  const confirm = useConfirm()
  const [busy, setBusy]         = useState(false)
  const [printing, setPrinting] = useState(false)
  const [err, setErr]           = useState(null)
  const canManage = !!(file && op && request)

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [onClose])

  // Print: fetch the PDF as a blob, load into a hidden same-origin iframe,
  // then call contentWindow.print() — works for both same-origin proxy URLs
  // and cross-origin Wasabi presigned URLs (CORS is configured on the bucket).
  // Drive embeds fall back to opening in a new tab.
  async function handlePrint() {
    if (printing) return
    const fetchUrl = viewUrl || inlineUrl
    if (!fetchUrl) return
    if (/drive\.google\.com|docs\.google\.com/.test(fetchUrl)) {
      window.open(fetchUrl, '_blank')
      return
    }
    setPrinting(true)
    try {
      const resp = await fetch(fetchUrl)
      if (!resp.ok) { window.open(fetchUrl, '_blank'); return }
      const blob   = await resp.blob()
      const objUrl = URL.createObjectURL(blob)
      const iframe = document.createElement('iframe')
      iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:1280px;height:900px;border:none'
      iframe.src = objUrl
      document.body.appendChild(iframe)
      iframe.addEventListener('load', () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print() } catch {
          window.open(objUrl, '_blank')
        }
        setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(objUrl) }, 30000)
      })
    } catch {
      window.open(fetchUrl, '_blank')
    } finally {
      setPrinting(false)
    }
  }

  async function inactivate() {
    if (!canManage) return
    if (!await confirm({
      title: 'Inactivate ไฟล์',
      message: `"${file.filename}" จะถูกซ่อนจาก HR portal — ยังเก็บไฟล์ไว้ในระบบ`,
      confirmLabel: 'Inactivate',
      tone: 'warn',
    })) return
    setBusy(true); setErr(null)
    try {
      await api.operationDocuments.deleteFile(op.id, request.id, file.id)
      onChanged?.()
      onClose()
    } catch (e) { setErr(extractErr(e)); setBusy(false) }
  }
  async function permanentDelete() {
    if (!canManage) return
    if (!await confirm({
      title: 'ลบไฟล์ถาวร',
      message: `"${file.filename}" จะถูกลบออกจากระบบและ Wasabi อย่างถาวร — กู้คืนไม่ได้`,
      confirmLabel: 'ลบถาวร',
      tone: 'danger',
    })) return
    setBusy(true); setErr(null)
    try {
      await api.operationDocuments.deleteFile(op.id, request.id, file.id, { permanent: true })
      onChanged?.()
      onClose()
    } catch (e) { setErr(extractErr(e)); setBusy(false) }
  }

  const isImage = typeof contentType === 'string' && contentType.startsWith('image/')
  const isPdf   = typeof contentType === 'string' && contentType === 'application/pdf'
  // Drive embed URLs MUST use iframe — Drive doesn't expose CORS-fetchable
  // bytes for PDF.js. Wasabi-hosted PDFs (which arrive through our proxy
  // and are same-origin + CORS-friendly) use PDF.js so Arc Shields and
  // other privacy extensions can't block the embed.
  const isDriveEmbed = /(?:drive|docs?)\.google\.com\//.test(inlineUrl || '')

  // "open in new tab" uses viewUrl (no Content-Disposition) so PDFs open inline
  // in the browser rather than triggering a download. Falls back to inlineUrl.
  const openUrl = viewUrl || inlineUrl

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-zinc-900/85 backdrop-blur-sm">
      <header className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-zinc-900/60 text-white">
        <span className="text-[12.5px] md:text-[13px] truncate flex-1 font-medium" title={filename}>{filename || 'preview'}</span>
        {downloadUrl && (
          <a href={downloadUrl} download={filename || true}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] bg-white/10 hover:bg-white/20 transition">
            <Download size={12} /> ดาวน์โหลด
          </a>
        )}
        {openUrl && (
          <a href={openUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] bg-white/10 hover:bg-white/20 transition">
            <ExternalLink size={12} /> เปิดในแท็บใหม่
          </a>
        )}
        <button onClick={handlePrint} disabled={printing}
          title="พิมพ์เอกสาร"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] bg-white/10 hover:bg-white/20 transition disabled:opacity-50">
          {printing ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
          พิมพ์
        </button>
        {canManage && (
          <>
            <span className="w-px h-5 bg-white/20 mx-0.5" />
            <button onClick={inactivate} disabled={busy}
              title="ซ่อนจาก HR portal"
              className="group inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] bg-white/10 hover:bg-white/20 transition disabled:opacity-50">
              <Eye    size={12} className="group-hover:hidden" />
              <EyeOff size={12} className="hidden group-hover:inline-block" />
              ซ่อนจาก HR
            </button>
            <button onClick={permanentDelete} disabled={busy}
              title="ลบถาวร"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] bg-rose-500/30 hover:bg-rose-500/50 text-rose-100 transition disabled:opacity-50">
              <Trash2 size={12} /> ลบ
            </button>
          </>
        )}
        <button onClick={onClose}
          className="w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-white/15">
          <X size={18} />
        </button>
      </header>

      {err && (
        <div className="px-3 md:px-4 py-2 bg-rose-500/20 text-rose-100 text-[12px] inline-flex items-center gap-1.5">
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      <div onClick={onClose} className="flex-1 overflow-auto flex items-center justify-center p-3 md:p-6">
        {!inlineUrl ? (
          <div className="text-white/80 text-center text-[13px] max-w-[320px]">
            ยังไม่มีลิงก์สำหรับ preview — ใช้ปุ่ม "เปิดในแท็บใหม่" หรือ "ดาวน์โหลด" ด้านบน
          </div>
        ) : isImage ? (
          <img src={inlineUrl} alt={filename}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-md shadow-2xl" />
        ) : isPdf && !isDriveEmbed ? (
          // Wasabi-hosted PDF — render via PDF.js (canvas) so Arc Shields /
          // browser-level iframe blockers can't intercept it.
          <PdfViewer url={inlineUrl} />
        ) : isDriveEmbed ? (
          <div onClick={(e) => e.stopPropagation()}
            className="relative w-full h-full max-w-[1100px] bg-white rounded-md shadow-2xl overflow-hidden">
            <iframe src={inlineUrl} title={filename}
              className="w-full h-full bg-white"
              sandbox="allow-scripts allow-same-origin allow-popups allow-downloads" />
          </div>
        ) : (
          <div className="bg-white rounded-lg p-6 max-w-[420px] text-center space-y-3" onClick={(e) => e.stopPropagation()}>
            <FileText size={32} className="mx-auto text-zinc-300" />
            <div className="text-[13px] text-zinc-700">เบราว์เซอร์ไม่รองรับการ preview ไฟล์ชนิดนี้</div>
            <a href={downloadUrl || externalUrl} download={filename || true}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium">
              <Download size={13} /> ดาวน์โหลดไฟล์
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

// Bundle every live file of a document request into a zip and trigger a
// browser download. Shared by the FilesDrawer and the inline RightFilesCard.
async function downloadFilesAsZip(op, request, files) {
  if (!files || files.length === 0) return
  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  await Promise.all(files.map(async (f) => {
    // Prefer the URL the list endpoint already presigned; fall back to a
    // per-file fetch if it wasn't included (e.g. older deployment).
    let url = f.url
    if (!url) {
      const meta = await api.operationDocuments.getFile(op.id, request.id, f.id)
      url = meta.url
    }
    const blob = await fetch(url).then((r) => r.blob())
    const safe = (f.filename || `file-${f.id}`).replace(/[\/\\]/g, '_')
    zip.file(safe, blob)
  }))
  const out = await zip.generateAsync({ type: 'blob' })
  const dlUrl = URL.createObjectURL(out)
  const a = document.createElement('a')
  a.href = dlUrl
  a.download = `${slug(request.kind_name || 'documents')}-${op.id}.zip`
  a.click()
  setTimeout(() => URL.revokeObjectURL(dlUrl), 5000)
}

// "https://drive.google.com/file/d/<id>/view?usp=drivesdk"  →  "<id>"
// or "?id=<id>&...". Returns null on no match.
function driveIdFromUrl(url) {
  if (!url || typeof url !== 'string') return null
  const m1 = url.match(/\/d\/([a-zA-Z0-9_-]+)/)
  if (m1) return m1[1]
  const m2 = url.match(/[?&]id=([a-zA-Z0-9_-]+)/)
  if (m2) return m2[1]
  return null
}


/* ──────────────────────────────── Files drawer ─────────────────────────── */

function FilesDrawer({ op, request: r, onClose, onChange }) {
  const confirm = useConfirm()
  const fileInputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [downloadingAll, setDownloadingAll] = useState(false)
  const [err, setErr]   = useState(null)
  const [showInactive, setShowInactive] = useState(true)
  const [dragOver, setDragOver] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)   // file row or null

  const live     = r.files         || []
  const inactive = r.deleted_files || []
  const atCap = live.length >= 5

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [onClose])

  function pickFile() { fileInputRef.current?.click() }

  async function uploadFile(file) {
    if (!file) return
    if (atCap) { setErr('ไฟล์ครบ 5 ไฟล์แล้ว — ลบของเก่าก่อน'); return }
    if (file.size > 10 * 1024 * 1024) { setErr('ไฟล์ใหญ่เกิน 10 MB'); return }
    if (!/^image\/(jpeg|png)$|^application\/pdf$/.test(file.type)) { setErr('รองรับเฉพาะ JPG, PNG, PDF'); return }
    setBusy(true); setErr(null)
    try {
      const dataUri = await fileToDataUri(file)
      await api.operationDocuments.uploadFile(op.id, r.id, { data: dataUri, filename: file.name })
      onChange()
    } catch (e) {
      setErr(extractErr(e))
    } finally { setBusy(false) }
  }
  async function onFileChange(e) {
    const file = e.target.files?.[0]
    if (e.target) e.target.value = ''
    await uploadFile(file)
  }
  function onDragOver(e)  { e.preventDefault(); if (!atCap && !busy) setDragOver(true) }
  function onDragLeave(e) { e.preventDefault(); setDragOver(false) }
  async function onDrop(e) {
    e.preventDefault(); setDragOver(false)
    if (atCap || busy) return
    const file = e.dataTransfer?.files?.[0]
    await uploadFile(file)
  }
  async function downloadFile(f) {
    try {
      const r2 = await api.operationDocuments.getFile(op.id, r.id, f.id)
      window.open(r2.download_url || r2.url, '_self')
    } catch (e) { setErr(extractErr(e)) }
  }
  async function inactivateFile(f) {
    if (!await confirm({
      title: 'Inactivate ไฟล์',
      message: `"${f.filename}" จะถูกซ่อนจาก HR portal — ยังเก็บไฟล์ไว้ในระบบ`,
      confirmLabel: 'Inactivate',
      tone: 'warn',
    })) return
    setBusy(true); setErr(null)
    try { await api.operationDocuments.deleteFile(op.id, r.id, f.id); onChange() }
    catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }
  async function restoreFile(f) {
    setBusy(true); setErr(null)
    try { await api.operationDocuments.restoreFile(op.id, r.id, f.id); onChange() }
    catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }
  async function permanentDelete(f) {
    if (!await confirm({
      title: 'ลบไฟล์ถาวร',
      message: `"${f.filename}" จะถูกลบออกจากระบบและ Wasabi อย่างถาวร — กู้คืนไม่ได้`,
      confirmLabel: 'ลบถาวร',
      tone: 'danger',
    })) return
    setBusy(true); setErr(null)
    try { await api.operationDocuments.deleteFile(op.id, r.id, f.id, { permanent: true }); onChange() }
    catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }
  async function downloadAll() {
    if (live.length === 0 || downloadingAll) return
    setDownloadingAll(true); setErr(null)
    try { await downloadFilesAsZip(op, r, live) }
    catch (e) { setErr(extractErr(e)) }
    finally   { setDownloadingAll(false) }
  }

  return (
    <div className="fixed inset-0 z-40">
      <div onClick={onClose} className="absolute inset-0 bg-zinc-900/30 backdrop-blur-[1px]" />
      <aside className="absolute right-0 top-0 h-full w-[520px] max-w-[100vw] bg-white shadow-2xl ring-1 ring-zinc-200 flex flex-col">
        <header className="px-5 py-4 border-b border-zinc-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700 font-semibold">{r.kind_type === 'generated' ? 'Signed copies' : 'Files'}</div>
            <h2 className="text-[15px] font-semibold text-zinc-900 truncate">{displayLabel(r)}</h2>
            <p className="text-[11.5px] text-zinc-500 mt-0.5">{live.length} active{inactive.length ? ` · ${inactive.length} inactive` : ''}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 inline-flex items-center justify-center">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Live files */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[12px] uppercase tracking-wider font-semibold text-zinc-500">ไฟล์ที่ใช้งานอยู่ ({live.length}/5)</h3>
              {live.length > 1 && (
                <button onClick={downloadAll} disabled={downloadingAll || busy}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ring-1 ring-amber-200 hover:bg-amber-50 text-amber-800 text-[12px] font-medium disabled:opacity-60">
                  {downloadingAll ? <Loader2 size={12} className="animate-spin" /> : <FolderDown size={12} />}
                  ดาวน์โหลดทั้งหมด (.zip)
                </button>
              )}
            </div>
            {live.length === 0 ? (
              <p className="text-[12.5px] text-zinc-400 italic">ยังไม่มีไฟล์</p>
            ) : (
              <ul className="space-y-1.5">
                {live.map((f) => (
                  <li key={f.id}>
                    <FileRow file={f} busy={busy}
                      onPreview={() => setPreviewFile(f)}
                      actions={[
                        { icon: Download, label: 'ดาวน์โหลด', onClick: () => downloadFile(f) },
                        // State icon = Eye (currently visible); hover swaps
                        // to EyeOff + amber tint to preview the hide action.
                        { icon: Eye, hoverIcon: EyeOff, hoverTone: 'amber',
                          label: 'ซ่อนจาก HR', onClick: () => inactivateFile(f) },
                        { icon: Trash2,   label: 'ลบถาวร',     onClick: () => permanentDelete(f), danger: true },
                      ]} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Upload — click anywhere or drag-and-drop a file onto the dashed area */}
          <section className="border-t border-zinc-100 pt-3">
            <h3 className="text-[12px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">อัปโหลด (แทน HR)</h3>
            <button type="button" onClick={pickFile} disabled={busy || atCap}
              onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
              className={'w-full px-4 py-6 rounded-xl border-2 border-dashed text-center transition-colors flex flex-col items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed ' +
                (atCap
                  ? 'border-zinc-200 bg-zinc-50/40 text-zinc-400 cursor-not-allowed'
                  : dragOver
                    ? 'border-amber-500 bg-amber-50 text-amber-900'
                    : 'border-zinc-300 bg-zinc-50/40 hover:border-amber-400 hover:bg-amber-50/40 text-zinc-600')}>
              {busy
                ? <Loader2 size={22} className="animate-spin text-amber-500" />
                : <Upload  size={22} className={dragOver ? 'text-amber-600' : 'text-zinc-400'} />}
              <span className="text-[13px] font-medium">
                {busy      ? 'กำลังอัปโหลด…'
                 : dragOver ? 'วางไฟล์เพื่ออัปโหลด'
                 : atCap    ? 'ไฟล์ครบ 5 ไฟล์แล้ว'
                            : <>คลิกเพื่อเลือกไฟล์ <span className="text-zinc-400 font-normal">หรือลากไฟล์มาวางที่นี่</span></>}
              </span>
              <span className="text-[11px] text-zinc-400">JPG / PNG / PDF · ≤ 10 MB · เก็บได้สูงสุด 5 ไฟล์</span>
            </button>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf" className="hidden" onChange={onFileChange} />
          </section>

          {/* Inactive */}
          {inactive.length > 0 && (
            <section className="border-t border-zinc-100 pt-3">
              <button onClick={() => setShowInactive((s) => !s)}
                className="w-full flex items-center justify-between mb-2 text-[12px] uppercase tracking-wider font-semibold text-zinc-500 hover:text-zinc-900">
                <span className="inline-flex items-center gap-1.5">
                  <EyeOff size={11} /> Inactive ({inactive.length})
                </span>
                <ChevronDown size={12} className={'transition-transform ' + (showInactive ? 'rotate-180' : '')} />
              </button>
              {showInactive && (
                <ul className="space-y-1.5">
                  {inactive.map((f) => (
                    <li key={f.id}>
                      <FileRow file={f} deleted busy={busy}
                        onPreview={() => setPreviewFile(f)}
                        actions={[
                          // State icon = EyeOff (currently hidden); hover
                          // swaps to Eye + emerald tint to preview the restore.
                          { icon: EyeOff, hoverIcon: Eye, hoverTone: 'emerald',
                            label: 'แสดงให้ HR', onClick: () => restoreFile(f) },
                          { icon: Trash2,    label: 'ลบถาวร',    onClick: () => permanentDelete(f), danger: true },
                        ]} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {err && (
            <div className="px-2.5 py-1.5 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[11.5px] inline-flex items-center gap-1.5">
              <AlertTriangle size={11} /> {err}
            </div>
          )}
        </div>
      </aside>

      {previewFile && (
        <FilePreviewModal
          file={previewFile} op={op} request={r}
          filename={previewFile.filename}
          contentType={previewFile.content_type}
          inlineUrl={`/api/operations/${op.id}/documents/${r.id}/files/${previewFile.id}/preview`}
          externalUrl={previewFile.url}
          downloadUrl={previewFile.download_url || previewFile.url}
          onClose={() => setPreviewFile(null)}
          onChanged={onChange}
        />
      )}
    </div>
  )
}

function FileRow({ file, deleted, actions = [], busy, onPreview }) {
  const meta = (
    <>
      <div className={'text-[12.5px] truncate ' + (deleted ? 'line-through text-zinc-500' : 'text-zinc-900 font-medium')}>{file.filename}</div>
      <div className="text-[10.5px] text-zinc-500 tabular-nums">
        {(file.size_bytes / 1024).toFixed(0)} KB · {fmtDate(file.uploaded_at)}
        {file.uploaded_by_role === 'hr' && <span className="ml-1.5 px-1 rounded bg-amber-100 text-amber-800">HR</span>}
        {deleted && (
          <span className="ml-1.5 italic">
            · inactivated โดย {file.deleted_by_role === 'hr' ? 'HR' : 'staff'} {fmtDate(file.deleted_at)}
            {file.deleted_reason ? ` (${file.deleted_reason})` : ''}
          </span>
        )}
      </div>
    </>
  )
  return (
    <div className={'flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-zinc-50 transition ' +
      (deleted ? 'opacity-60' : '')}>
      <FileTypeIcon contentType={file.content_type} />
      {onPreview ? (
        <button type="button" onClick={onPreview} title="ดู preview"
          className="min-w-0 flex-1 text-left rounded -mx-1 px-1 hover:bg-amber-50/60 focus:outline-none focus:bg-amber-50/80 transition">
          {meta}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{meta}</div>
      )}
      <div className="shrink-0 flex items-center gap-0.5">
        {actions.map((a, i) => <FileRowActionButton key={i} action={a} busy={busy} />)}
      </div>
    </div>
  )
}

function FileRowActionButton({ action: a, busy }) {
  const Hover = a.hoverIcon
  const tone =
    a.danger             ? 'text-zinc-400 hover:text-rose-600   hover:bg-rose-50'   :
    a.hoverTone === 'emerald' ? 'text-zinc-400 hover:text-emerald-700 hover:bg-emerald-50' :
    a.hoverTone === 'amber'   ? 'text-zinc-500 hover:text-amber-700   hover:bg-amber-50'   :
                                'text-zinc-500 hover:text-amber-700   hover:bg-amber-50'
  return (
    <button onClick={a.onClick} disabled={busy} title={a.label}
      className={'group w-8 h-8 rounded-md inline-flex items-center justify-center disabled:opacity-50 transition-colors ' + tone}>
      {Hover ? (
        // Icon mirrors current STATE; hover previews the action by swapping
        // to the alternate icon — no React state needed, pure CSS swap.
        <>
          <a.icon size={12} className="group-hover:hidden" />
          <Hover  size={12} className="hidden group-hover:inline-block" />
        </>
      ) : (
        <a.icon size={12} />
      )}
    </button>
  )
}


/* ──────────────────────────────── Generate modal ─────────────────────────
 *  Per-template forms. Today only "service-agreement" (slug) → 'mou' template
 *  is wired. Auto fields are previewed read-only (resolved from op + company),
 *  custom fields editable.
 * ────────────────────────────────────────────────────────────────────────── */

const SUPPORTED_TEMPLATES = {
  'service-agreement':           'mou',
  'sso-medical-request':         'sso_request',
  'external-area-request':       'sso_outside_mobileDental',
  'pho-firstpage':               'PHO_firstpage',
  'pho-spo4':                    'PHO_SPO4',
  'pho-authorize-transfer':      'PHO_authorize_transfer',
  'sso-service-notification':    'SSO_service_notification',
  'sso-employee-list':           'SSO_employee_list',
  'pho-other-bus-request':       'PHO_other_bus_request',   // backend pipeline merges N pages
  // pdf-fill kinds — backend dispatches via slug, no Apps Script involved.
  'checklist-sso-mobile-health': 'checklist_sso_mobile_health',
  'on-bus-announcement':         'onBus_announcement',
}

function GenerateModal({ r, op, onClose, onGenerated }) {
  const template = SUPPORTED_TEMPLATES[r.kind_slug]
  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-zinc-900/40 backdrop-blur-[1px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[560px] max-h-[92vh] flex flex-col">
        <header className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700 font-semibold">สร้างเอกสาร</div>
            <h2 className="text-[15px] font-bold text-zinc-900">{r.kind_name}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 inline-flex items-center justify-center">
            <X size={16} />
          </button>
        </header>
        {template === 'mou'                          && <MouForm                  r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'sso_request'                  && <SsoRequestForm           r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'sso_outside_mobileDental'     && <ExternalAreaRequestForm  r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'PHO_firstpage'                && <PhoFirstpageForm         r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'PHO_SPO4'                     && <PhoSpo4Form              r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'PHO_authorize_transfer'       && <PhoAuthorizeTransferForm r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'SSO_service_notification'     && <SsoServiceNotificationForm r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'SSO_employee_list'            && <SsoEmployeeListForm        r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'PHO_other_bus_request'        && <PhoOtherBusRequestForm     r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'checklist_sso_mobile_health'  && <ChecklistSsoForm         r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {template === 'onBus_announcement'           && <OnBusAnnouncementForm    r={r} op={op} onClose={onClose} onGenerated={onGenerated} />}
        {!template                                   && <UnsupportedTemplate kindSlug={r.kind_slug} onClose={onClose} />}
      </div>
    </div>
  )
}

function UnsupportedTemplate({ kindSlug, onClose }) {
  return (
    <div className="p-5">
      <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/50 px-3 py-2.5 text-[12.5px] text-amber-900 leading-relaxed">
        เอกสารชนิด <code className="bg-white px-1 rounded">{kindSlug}</code> ยังไม่มี Apps Script template — แจ้งทีมพัฒนาเพื่อเพิ่ม
      </div>
      <div className="mt-4 flex justify-end">
        <button onClick={onClose} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100">ปิด</button>
      </div>
    </div>
  )
}

function MouForm({ r, op, onClose, onGenerated }) {
  // Seed auto fields from operation/company; staff can edit any of them just
  // for this generation without touching the source records.
  const primary = (op.contacts || []).find((c) => c.is_primary) || {}
  const dateDefault = op.start_date && op.end_date
    ? (op.start_date === op.end_date
        ? fmtThaiBeDate(op.start_date)
        : `${fmtThaiBeDate(op.start_date)} จนถึง ${fmtThaiBeDate(op.end_date)}`)
    : ''
  const timeDefault = op.start_time && op.end_time ? `${op.start_time} จนถึง ${op.end_time}` : ''

  const [companyName,      setCompanyName]      = useState(op.official_company_name || op.company_name || '')
  const [companyAddress,   setCompanyAddress]   = useState(op.company_address || op.location_address || '')
  const [dateStr,          setDateStr]          = useState(dateDefault)
  const [timeStr,          setTimeStr]          = useState(timeDefault)
  const [employeeN,        setEmployeeN]        = useState(op.expected_patients != null ? String(op.expected_patients) : '')
  const [cpName,           setCpName]           = useState(primary.fullname || primary.name || '')
  const [cpPhone,          setCpPhone]          = useState(primary.phone || '')

  const [chairmanN,        setChairmanN]        = useState(1)
  const [contractYear,     setContractYear]     = useState(1)
  const [contractLimit,    setContractLimit]    = useState(true)
  const [serviceMinimum,   setServiceMinimum]   = useState(50)
  const [busy, setBusy]                          = useState(false)
  const [err, setErr]                            = useState(null)

  const isRegenerating = !!r.generated_url
  const startBeYear    = ceYearOf(op.start_date) + 543
  const endBeYear      = startBeYear + (Number.isFinite(+contractYear) ? +contractYear : 0)

  async function generate() {
    if (busy) return
    setErr(null); setBusy(true)
    try {
      const body = {
        // Custom fields (staff input)
        contract_year:   parseInt(contractYear, 10) || 1,
        contract_limit:  !!contractLimit,
        service_minimum: contractLimit ? (parseInt(serviceMinimum, 10) || 50) : null,
        // Overrides — backend uses these in place of DB-derived values when present.
        company_chairmanN: parseInt(chairmanN, 10) || 1,
        company_name:     companyName.trim()    || undefined,
        company_address:  companyAddress.trim() || undefined,
        date:             dateStr.trim()        || undefined,
        time:             timeStr.trim()        || undefined,
        employee_n:       employeeN.trim()      || undefined,
        company_cp_name:  cpName.trim()         || undefined,
        company_cp_phone: cpPhone.trim()        || undefined,
      }
      await api.operationDocuments.generate(op.id, r.id, body)
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        {/* Document fields — auto-filled from Operation but editable. */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ข้อมูลที่จะใช้ในเอกสาร</span>
            <span className="text-[10.5px] text-zinc-400">แก้ไขได้เฉพาะเอกสารนี้ — ไม่กระทบ Operation</span>
          </div>
          <div className="space-y-2.5">
            <GenField label="บริษัท"            value={companyName}    onChange={setCompanyName} />
            <GenField label="ที่อยู่"            value={companyAddress} onChange={setCompanyAddress} textarea addrSrc={{ company: op.company_address, operation: op.location_address }} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <GenField label="วันที่"          value={dateStr}        onChange={setDateStr} />
              <GenField label="เวลา"           value={timeStr}        onChange={setTimeStr} />
              <GenField label="คนไข้คาดการณ์"    value={employeeN}      onChange={setEmployeeN} tabular />
              <GenField label="ผู้ประสานงาน"     value={cpName}         onChange={setCpName} />
              <GenField label="เบอร์โทร"        value={cpPhone}        onChange={setCpPhone} />
            </div>
          </div>
        </div>

        {/* Contract-specific fields */}
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">รายละเอียดสัญญา</div>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block mb-1 text-[12px] text-zinc-700 font-medium">ระยะเวลาสัญญา (ปี)</span>
                <input type="number" min={1} max={5} value={contractYear}
                  onChange={(e) => setContractYear(e.target.value)}
                  className="block w-full px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[13px] tabular-nums" />
              </label>
              <label className="block">
                <span className="block mb-1 text-[12px] text-zinc-700 font-medium">สิ้นสุดสัญญา (พ.ศ.)</span>
                <div className="px-2.5 py-1.5 rounded-md bg-zinc-50 ring-1 ring-zinc-200 text-[13px] tabular-nums text-zinc-700">{endBeYear || '—'}</div>
              </label>
            </div>


            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={contractLimit}
                    onChange={(e) => setContractLimit(e.target.checked)}
                    className="accent-amber-500 w-3.5 h-3.5" />
                  <span className="text-[12.5px] text-zinc-800">กำหนดจำนวนผู้รับบริการขั้นต่ำ</span>
                </label>

                {contractLimit && (
                  <label className="block ml-6">
                    <span className="block mb-1 text-[12px] text-zinc-700 font-medium">จำนวนขั้นต่ำ (คน)</span>
                    <input type="number" min={1} value={serviceMinimum}
                      onChange={(e) => setServiceMinimum(e.target.value)}
                      className="block w-full sm:w-40 px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[13px] tabular-nums" />
                  </label>
                )}
              </div>

              <div>
                <span className="block mb-1 text-[12px] text-zinc-700 font-medium">จำนวนกรรมการบริษัท</span>
                <div className="flex gap-1.5">
                  {[1, 2].map((n) => (
                    <button key={n} type="button"
                      onClick={() => setChairmanN(n)}
                      className={`flex-1 py-1.5 rounded-md text-[13px] font-semibold tabular-nums border transition-colors ${
                        chairmanN === n
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'bg-white border-zinc-200 text-zinc-700 hover:border-amber-300'
                      }`}>{n} คน</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์จะถูกสร้างเป็น PDF บน Google Drive</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

function SsoRequestForm({ r, op, onClose, onGenerated }) {
  const primary = (op.contacts || []).find((c) => c.is_primary) || {}
  const dateDefault = op.start_date && op.end_date
    ? (op.start_date === op.end_date
        ? fmtThaiBeDate(op.start_date)
        : `${fmtThaiBeDate(op.start_date)} จนถึง ${fmtThaiBeDate(op.end_date)}`)
    : ''
  const timeDefault = op.start_time && op.end_time ? `${op.start_time} จนถึง ${op.end_time}` : ''
  const ssoNameDefault = formalSsoName(op.company_province, op.company_sso_district)

  // SSO-specific (top of form)
  const [ssoName,        setSsoName]        = useState(ssoNameDefault)
  const [companySsoNumber, setCompanySsoNumber] = useState(op.company_sso_employer_account || '')
  const [companyAuth,    setCompanyAuth]    = useState(op.company_authorized_signer_name || '')
  // Shared
  const [companyName,    setCompanyName]    = useState(op.official_company_name || op.company_name || '')
  const [companyAddress, setCompanyAddress] = useState(op.location_address || op.company_address || '')
  const [dateStr,        setDateStr]        = useState(dateDefault)
  const [timeStr,        setTimeStr]        = useState(timeDefault)
  const [employeeN,      setEmployeeN]      = useState(op.expected_patients != null ? String(op.expected_patients) : '')
  const [cpName,         setCpName]         = useState(primary.fullname || primary.name || '')
  const [cpPhone,        setCpPhone]        = useState(primary.phone || '')

  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const isRegenerating  = !!r.generated_url

  async function generate() {
    if (busy) return
    setErr(null); setBusy(true)
    try {
      const body = {
        // SSO-specific overrides
        sso_name:           ssoName.trim()        || undefined,
        company_sso_number: companySsoNumber.trim() || undefined,
        company_auth:       companyAuth.trim()    || undefined,
        // Shared overrides
        company_name:       companyName.trim()    || undefined,
        company_address:    companyAddress.trim() || undefined,
        date:               dateStr.trim()        || undefined,
        time:               timeStr.trim()        || undefined,
        employee_n:         employeeN.trim()      || undefined,
        company_cp_name:    cpName.trim()         || undefined,
        company_cp_phone:   cpPhone.trim()        || undefined,
      }
      await api.operationDocuments.generate(op.id, r.id, body)
      onGenerated()
    } catch (e) { setErr(extractErr(e)) }
    finally     { setBusy(false) }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ข้อมูลสำหรับยื่นประกันสังคม</span>
            <span className="text-[10.5px] text-zinc-400">แก้ไขได้เฉพาะเอกสารนี้</span>
          </div>
          <div className="space-y-2.5">
            <GenField label="สำนักงานประกันสังคม"      value={ssoName}         onChange={setSsoName} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <GenField label="เลขบัญชีนายจ้าง"          value={companySsoNumber} onChange={setCompanySsoNumber} tabular />
              <GenField label="ผู้มีอำนาจลงนาม"           value={companyAuth}      onChange={setCompanyAuth} />
            </div>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">ข้อมูลบริษัท / กิจกรรม</div>
          <div className="space-y-2.5">
            <GenField label="บริษัท"            value={companyName}    onChange={setCompanyName} />
            <GenField label="ที่อยู่"            value={companyAddress} onChange={setCompanyAddress} textarea addrSrc={{ company: op.company_address, operation: op.location_address }} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <GenField label="วันที่"          value={dateStr}        onChange={setDateStr} />
              <GenField label="เวลา"           value={timeStr}        onChange={setTimeStr} />
              <GenField label="คนไข้คาดการณ์"    value={employeeN}      onChange={setEmployeeN} tabular />
              <GenField label="ผู้ประสานงาน"     value={cpName}         onChange={setCpName} />
              <GenField label="เบอร์โทร"        value={cpPhone}        onChange={setCpPhone} />
            </div>
          </div>
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์จะถูกสร้างเป็น PDF บน Google Drive</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

// "เอกสารขอรับบริการรถทันตกรรมจากหน่วยงานภายนอกพื้นที่" — Apps Script template
// `sso_outside_mobileDental`. Auto-fills company name / authorized signer /
// address / date range from the operation+company; staff can edit any field
// for this generation only.
function ExternalAreaRequestForm({ r, op, onClose, onGenerated }) {
  const dateDefault = op.start_date && op.end_date
    ? (op.start_date === op.end_date
        ? fmtThaiBeDate(op.start_date)
        : `${fmtThaiBeDate(op.start_date)} จนถึง ${fmtThaiBeDate(op.end_date)}`)
    : ''

  const [companyName,    setCompanyName]    = useState(op.official_company_name || op.company_name || '')
  const [companyAuth,    setCompanyAuth]    = useState(op.company_authorized_signer_name || '')
  const [companyAddress, setCompanyAddress] = useState(op.location_address || op.company_address || '')
  const [dateStr,        setDateStr]        = useState(dateDefault)
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const isRegenerating  = !!r.generated_url

  async function generate() {
    if (busy) return
    setErr(null); setBusy(true)
    try {
      const body = {
        company_name:    companyName.trim()    || undefined,
        company_auth:    companyAuth.trim()    || undefined,
        company_address: companyAddress.trim() || undefined,
        date:            dateStr.trim()        || undefined,
      }
      await api.operationDocuments.generate(op.id, r.id, body)
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ข้อมูลที่จะใช้ในเอกสาร</span>
            <span className="text-[10.5px] text-zinc-400">แก้ไขได้เฉพาะเอกสารนี้ — ไม่กระทบ Operation</span>
          </div>
          <div className="space-y-2.5">
            <GenField label="บริษัท"            value={companyName}    onChange={setCompanyName} />
            <GenField label="ผู้มีอำนาจลงนาม"     value={companyAuth}    onChange={setCompanyAuth} />
            <GenField label="ที่อยู่บริษัท"        value={companyAddress} onChange={setCompanyAddress} textarea addrSrc={{ company: op.company_address, operation: op.location_address }} />
            <GenField label="วันที่"             value={dateStr}        onChange={setDateStr} />
          </div>
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์จะถูกสร้างเป็น PDF บน Google Drive</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

// "ใบปะหน้าเอกสารขออนุญาต สสจ." — Apps Script template `PHO_firstpage`.
// Unique inputs: unique_id (auto-generated TT-number), province.
function PhoFirstpageForm({ r, op, onClose, onGenerated }) {
  const dateDefault = op.start_date && op.end_date
    ? (op.start_date === op.end_date
        ? fmtThaiBeDate(op.start_date)
        : `${fmtThaiBeDate(op.start_date)} จนถึง ${fmtThaiBeDate(op.end_date)}`)
    : ''
  const timeDefault = op.start_time && op.end_time ? `${op.start_time} จนถึง ${op.end_time}` : ''

  // province: prefer operation location, fall back to company province
  const provinceDefault = (op.location_province || op.company_province || '').trim()

  // document number auto-generated by backend; show editable preview here so staff
  // can override before generating (e.g. for reissued docs)
  const startBeYear = op.start_date ? parseInt(op.start_date.slice(0, 4), 10) + 543 : new Date().getFullYear() + 543
  const uniqueIdDefault = `TT${String(r.id).padStart(4, '0')}/${startBeYear}`

  const [companyName,    setCompanyName]    = useState(op.official_company_name || op.company_name || '')
  const [companyAddress, setCompanyAddress] = useState(op.location_address || op.company_address || '')
  const [dateStr,        setDateStr]        = useState(dateDefault)
  const [timeStr,        setTimeStr]        = useState(timeDefault)
  const [province,       setProvince]       = useState(provinceDefault)
  const [uniqueId,       setUniqueId]       = useState(uniqueIdDefault)

  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const isRegenerating  = !!r.generated_url



  async function generate() {
    if (busy) return
    setErr(null); setBusy(true)
    try {
      await api.operationDocuments.generate(op.id, r.id, {
        company_name:    companyName.trim()    || undefined,
        company_address: companyAddress.trim() || undefined,
        date:            dateStr.trim()        || undefined,
        time:            timeStr.trim()        || undefined,
        province:        province.trim()       || undefined,
        unique_id:       uniqueId.trim()       || undefined,
      })
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ข้อมูลที่จะใช้ในเอกสาร</span>
            <span className="text-[10.5px] text-zinc-400">แก้ไขได้เฉพาะเอกสารนี้ — ไม่กระทบ Operation</span>
          </div>
          <div className="space-y-2.5">
            <GenField label="บริษัท"        value={companyName}    onChange={setCompanyName} />
            <GenField label="ที่อยู่บริษัท"  value={companyAddress} onChange={setCompanyAddress} textarea addrSrc={{ company: op.company_address, operation: op.location_address }} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <GenField label="วันที่"        value={dateStr}        onChange={setDateStr} />
              <GenField label="เวลา"         value={timeStr}        onChange={setTimeStr} />
            </div>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">ข้อมูลสำหรับ สสจ.</div>
          <div className="space-y-2.5">
            <GenField label="จังหวัด (สสจ.)" value={province} onChange={setProvince} />
            <GenField label="เลขที่เอกสาร" value={uniqueId} onChange={setUniqueId} />
            <p className="text-[10.5px] text-zinc-400 -mt-1">
              สร้างจากรหัส Request อัตโนมัติ — แก้ไขได้ถ้าต้องออกเลขใหม่
            </p>
          </div>
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์จะถูกสร้างเป็น PDF บน Google Drive</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

// "ใบ สพอ. 4" — Apps Script template `PHO_SPO4`.
// Doctor list + per-doctor day table are resolved server-side from
// operation_doctor_day + Xano /doctor; staff only sees scalar previews here.
function PhoSpo4Form({ r, op, onClose, onGenerated }) {
  // {{date}} always renders as a long-form range "<start> จนถึง <end>" with full
  // Thai month names; single-day ops repeat the same date on both sides.
  const dateDefault = fmtThaiBeDateLongRange(op.start_date, op.end_date)
  const timeDefault = op.start_time && op.end_time ? `${op.start_time} จนถึง ${op.end_time}` : ''

  const [companyName,    setCompanyName]    = useState(op.official_company_name || op.company_name || '')
  const [companyAddress, setCompanyAddress] = useState(op.location_address || op.company_address || '')
  const [dateStr,        setDateStr]        = useState(dateDefault)
  const [timeStr,        setTimeStr]        = useState(timeDefault)

  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const isRegenerating  = !!r.generated_url

  async function generate() {
    if (busy) return
    setErr(null); setBusy(true)
    try {
      await api.operationDocuments.generate(op.id, r.id, {
        company_name:    companyName.trim()    || undefined,
        company_address: companyAddress.trim() || undefined,
        date:            dateStr.trim()        || undefined,
        time:            timeStr.trim()        || undefined,
      })
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ข้อมูลที่จะใช้ในเอกสาร</span>
            <span className="text-[10.5px] text-zinc-400">แก้ไขได้เฉพาะเอกสารนี้ — ไม่กระทบ Operation</span>
          </div>
          <div className="space-y-2.5">
            <GenField label="บริษัท"        value={companyName}    onChange={setCompanyName} />
            <GenField label="ที่อยู่บริษัท"  value={companyAddress} onChange={setCompanyAddress} textarea addrSrc={{ company: op.company_address, operation: op.location_address }} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <GenField label="วันที่"        value={dateStr}        onChange={setDateStr} />
              <GenField label="เวลา"         value={timeStr}        onChange={setTimeStr} />
            </div>
          </div>
        </div>

        <div className="rounded-lg ring-1 ring-zinc-200 bg-zinc-50 px-3 py-2.5 text-[12px] text-zinc-700">
          <span className="font-medium text-zinc-800">รายชื่อทันตแพทย์</span> — ระบบจะดึงจากตารางหมอประจำวันของ Operation อัตโนมัติ
          (ทันตแพทย์ที่ซ้ำกันจะนับเป็นคนเดียว) และคำนวณ <span className="font-medium">วัน/เวลา ที่ปฏิบัติงาน</span> ของแต่ละคนให้
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์จะถูกสร้างเป็น PDF บน Google Drive</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

// "หนังสือมอบอำนาจ (สสจ.)" — Apps Script template `PHO_authorize_transfer`.
// Inputs: 4 rep fields (with select-from-history dropdown) + 2 witness fields
// (witness1 defaults to the current user, witness2 to a hard-coded name).
function PhoAuthorizeTransferForm({ r, op, onClose, onGenerated }) {
  const [reps, setReps]               = useState([])             // saved history
  const [showHistory, setShowHistory] = useState(false)
  const [meEmp, setMeEmp]             = useState(null)           // current user's Xano employee record

  const [repFullname, setRepFullname] = useState('')
  const [repIdCard,   setRepIdCard]   = useState('')
  const [repAddress,  setRepAddress]  = useState('')
  const [repPhone,    setRepPhone]    = useState('')
  const [witness1,    setWitness1]    = useState('')
  const [witness2,    setWitness2]    = useState('นายกฤตวิณัฐ อนันต์กิตติ')

  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const isRegenerating  = !!r.generated_url

  useEffect(() => {
    let cancelled = false
    async function loadHistory() {
      try {
        const list = await api.staffAuthorizeReps.list()
        if (!cancelled) setReps(Array.isArray(list) ? list : [])
      } catch (_) { /* non-fatal */ }
    }
    async function loadMe() {
      try {
        const emp = await api.staffMeEmployee()
        if (!cancelled && emp?.fullname) {
          setMeEmp(emp)
          setWitness1((cur) => cur || emp.fullname)
        }
      } catch (_) { /* non-fatal — backend will still default from session */ }
    }
    loadHistory(); loadMe()
    return () => { cancelled = true }
  }, [])

  function pickRep(rep) {
    setRepFullname(rep.fullname || '')
    setRepIdCard(rep.id_card    || '')
    setRepAddress(rep.address   || '')
    setRepPhone(rep.phone       || '')
    setShowHistory(false)
  }
  async function deleteRep(rep, ev) {
    ev.stopPropagation()
    try {
      await api.staffAuthorizeReps.remove(rep.id)
      setReps((xs) => xs.filter((x) => x.id !== rep.id))
    } catch (e) { setErr(extractErr(e)) }
  }

  function valid() {
    if (!repFullname.trim()) return 'กรุณากรอกชื่อผู้รับมอบอำนาจ'
    if (!/^\d{13}$/.test(repIdCard.replace(/\D/g, '')))  return 'เลขบัตรประชาชนต้อง 13 หลัก'
    if (!repAddress.trim())  return 'กรุณากรอกที่อยู่'
    if (!/^\d{10}$/.test(repPhone.replace(/\D/g, '')))   return 'เบอร์โทรต้อง 10 หลัก'
    return null
  }

  async function generate() {
    if (busy) return
    const v = valid(); if (v) { setErr(v); return }
    setErr(null); setBusy(true)
    try {
      await api.operationDocuments.generate(op.id, r.id, {
        rep_fullname:      repFullname.trim(),
        rep_idCard:        repIdCard.replace(/\D/g, ''),
        rep_address:       repAddress.trim(),
        rep_phone:         repPhone.replace(/\D/g, ''),
        witness1_fullname: witness1.trim() || (meEmp?.fullname || ''),
        witness2_fullname: witness2.trim() || 'นายกฤตวิณัฐ อนันต์กิตติ',
      })
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        {/* ── ผู้รับมอบอำนาจ ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ผู้รับมอบอำนาจ</span>
            <button type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="inline-flex items-center gap-1 text-[11.5px] text-amber-700 hover:text-amber-800">
              <ChevronDown size={12} className={'transition-transform ' + (showHistory ? 'rotate-180' : '')} />
              เลือกจากที่เคยใช้ ({reps.length})
            </button>
          </div>

          {showHistory && (
            <div className="mb-2 rounded-lg ring-1 ring-zinc-200 bg-white max-h-44 overflow-y-auto">
              {reps.length === 0 && (
                <div className="px-3 py-2.5 text-[12px] text-zinc-500">ยังไม่มีรายการที่เคยใช้</div>
              )}
              {reps.map((rep) => (
                <button key={rep.id} type="button" onClick={() => pickRep(rep)}
                  className="w-full text-left px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-medium text-zinc-800 truncate">{rep.fullname}</div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {rep.id_card} · {rep.phone}
                    </div>
                  </div>
                  <span onClick={(e) => deleteRep(rep, e)}
                    className="shrink-0 p-1 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600"
                    title="ลบรายการ">
                    <Trash2 size={13} />
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="space-y-2.5">
            <GenField label="ชื่อ-นามสกุล" value={repFullname} onChange={setRepFullname} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <GenField label="เลขบัตรประชาชน (13 หลัก)" value={repIdCard} onChange={(v) => setRepIdCard(v.replace(/\D/g, '').slice(0, 13))} />
              <GenField label="เบอร์โทร (10 หลัก)"      value={repPhone}  onChange={(v) => setRepPhone(v.replace(/\D/g, '').slice(0, 10))} />
            </div>
            <GenField label="ที่อยู่" value={repAddress} onChange={setRepAddress} textarea />
          </div>
        </div>

        {/* ── พยาน ── */}
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">พยาน</div>
          <div className="space-y-2.5">
            <GenField label="พยานคนที่ 1"
              value={witness1} onChange={setWitness1}
              placeholder={meEmp?.fullname ? `เว้นว่าง = ${meEmp.fullname}` : 'เว้นว่าง = ใช้ชื่อของคุณ'} />
            <GenField label="พยานคนที่ 2" value={witness2} onChange={setWitness2} />
          </div>
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ผู้รับมอบอำนาจจะถูกบันทึกให้เลือกใช้ครั้งถัดไป</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

// "แบบแจ้งการออกให้บริการ" (ประกันสังคม) — Apps Script template
// `SSO_service_notification`. Same shape as PhoSpo4Form but adds an
// employee_n field (จำนวนผู้ประกันตน).
function SsoServiceNotificationForm({ r, op, onClose, onGenerated }) {
  const dateDefault = op.start_date && op.end_date
    ? (op.start_date === op.end_date
        ? fmtThaiBeDate(op.start_date)
        : `${fmtThaiBeDate(op.start_date)} จนถึง ${fmtThaiBeDate(op.end_date)}`)
    : ''
  const timeDefault = op.start_time && op.end_time ? `${op.start_time} จนถึง ${op.end_time}` : ''

  const [companyName,    setCompanyName]    = useState(op.official_company_name || op.company_name || '')
  const [companyAddress, setCompanyAddress] = useState(op.location_address || op.company_address || '')
  const [dateStr,        setDateStr]        = useState(dateDefault)
  const [timeStr,        setTimeStr]        = useState(timeDefault)
  const [employeeN,      setEmployeeN]      = useState(op.expected_patients != null ? String(op.expected_patients) : '')

  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const isRegenerating  = !!r.generated_url

  async function generate() {
    if (busy) return
    setErr(null); setBusy(true)
    try {
      await api.operationDocuments.generate(op.id, r.id, {
        company_name:    companyName.trim()    || undefined,
        company_address: companyAddress.trim() || undefined,
        date:            dateStr.trim()        || undefined,
        time:            timeStr.trim()        || undefined,
        employee_n:      employeeN.trim()      || undefined,
      })
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ข้อมูลที่จะใช้ในเอกสาร</span>
            <span className="text-[10.5px] text-zinc-400">แก้ไขได้เฉพาะเอกสารนี้ — ไม่กระทบ Operation</span>
          </div>
          <div className="space-y-2.5">
            <GenField label="บริษัท"        value={companyName}    onChange={setCompanyName} />
            <GenField label="ที่อยู่บริษัท"  value={companyAddress} onChange={setCompanyAddress} textarea addrSrc={{ company: op.company_address, operation: op.location_address }} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <GenField label="วันที่"        value={dateStr}        onChange={setDateStr} />
              <GenField label="เวลา"         value={timeStr}        onChange={setTimeStr} />
            </div>
            <GenField label="จำนวนผู้ประกันตน (ราย)" value={employeeN} onChange={(v) => setEmployeeN(v.replace(/[^\d]/g, ''))} />
          </div>
        </div>

        <div className="rounded-lg ring-1 ring-zinc-200 bg-zinc-50 px-3 py-2.5 text-[12px] text-zinc-700">
          <span className="font-medium text-zinc-800">รายชื่อทันตแพทย์</span> — ระบบจะดึงจากตารางหมอประจำวันของ Operation อัตโนมัติ
          (ทันตแพทย์ที่ซ้ำกันจะนับเป็นคนเดียว)
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์จะถูกสร้างเป็น PDF บน Google Drive</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

// "รายชื่อผู้ต้องการรับบริการ" (ประกันสังคม) — Apps Script template
// `SSO_employee_list`. Reads patients from service_registration on the
// server. Only minimal scalars are editable here (company/date); the
// employee table is auto-derived. Shows live registration count so staff
// know whether the document can be generated at all.
function SsoEmployeeListForm({ r, op, onClose, onGenerated }) {
  const dateDefault = op.start_date && op.end_date
    ? (op.start_date === op.end_date
        ? fmtThaiBeDate(op.start_date)
        : `${fmtThaiBeDate(op.start_date)} จนถึง ${fmtThaiBeDate(op.end_date)}`)
    : ''

  const [companyName, setCompanyName] = useState(op.official_company_name || op.company_name || '')
  const [dateStr,     setDateStr]     = useState(dateDefault)
  const [regCount,    setRegCount]    = useState(null)   // null = not yet loaded
  const [busy, setBusy] = useState(false)
  const [err,  setErr]  = useState(null)
  const isRegenerating  = !!r.generated_url

  useEffect(() => {
    let cancelled = false
    async function loadCount() {
      try {
        const list = await api.operations.surveys.registrations(op.id)
        if (!cancelled) setRegCount(Array.isArray(list) ? list.length : 0)
      } catch {
        if (!cancelled) setRegCount(0)
      }
    }
    loadCount()
    return () => { cancelled = true }
  }, [op.id])

  async function generate() {
    if (busy) return
    setErr(null); setBusy(true)
    try {
      await api.operationDocuments.generate(op.id, r.id, {
        company_name: companyName.trim() || undefined,
        date:         dateStr.trim()     || undefined,
      })
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  const noRegs = regCount === 0
  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ข้อมูลที่จะใช้ในเอกสาร</span>
            <span className="text-[10.5px] text-zinc-400">แก้ไขได้เฉพาะเอกสารนี้ — ไม่กระทบ Operation</span>
          </div>
          <div className="space-y-2.5">
            <GenField label="บริษัท" value={companyName} onChange={setCompanyName} />
            <GenField label="วันที่"  value={dateStr}     onChange={setDateStr} />
          </div>
        </div>

        <div className={'rounded-lg ring-1 px-3 py-2.5 text-[12px] ' +
          (noRegs ? 'ring-rose-200 bg-rose-50 text-rose-800' : 'ring-zinc-200 bg-zinc-50 text-zinc-700')}>
          <span className="font-medium">รายชื่อผู้ลงทะเบียน</span>
          {regCount === null && <> — กำลังโหลด…</>}
          {regCount !== null && !noRegs && <> — ระบบจะดึงจาก service_registration ของ Operation อัตโนมัติ ({regCount} ราย)</>}
          {noRegs && <> — ยังไม่มีผู้ลงทะเบียน จึงไม่สามารถสร้างเอกสารนี้ได้</>}
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์จะถูกสร้างเป็น PDF บน Google Drive</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy || noRegs}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

// "หนังสือขอปฎิเสธจากรถทันตกรรมอื่นๆ" — multi-bus permission letter.
// One PDF page per other bus (in/out of สมาคม picks the template variant).
// Saved buses are filtered by the operation's sso_district; staff can pick
// any subset, add a new bus inline, or edit/delete existing entries.
function PhoOtherBusRequestForm({ r, op, onClose, onGenerated }) {
  const ssoDistrict = (op.sso_district || op.company_sso_district || '').trim()
  const [saved, setSaved]         = useState([])      // [{id, name, sso_district, in_association}]
  const [selectedIds, setSelIds]  = useState(new Set())
  const [adding, setAdding]       = useState(false)
  const [newName, setNewName]     = useState('')
  const [newInAssoc, setNewAssoc] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err,  setErr]  = useState(null)
  const isRegenerating  = !!r.generated_url

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const list = await api.otherMobileDentals.list(ssoDistrict)
        if (!cancelled) setSaved(Array.isArray(list) ? list : [])
      } catch (e) {
        if (!cancelled) setErr(extractErr(e))
      }
    }
    load()
    return () => { cancelled = true }
  }, [ssoDistrict])

  function toggle(id) {
    setSelIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  async function addBus() {
    const name = newName.trim()
    if (!name) return
    if (!ssoDistrict) { setErr('Operation ยังไม่ระบุประกันสังคมเขต — กรุณาตั้งค่าใน Operation ก่อน'); return }
    try {
      const row = await api.otherMobileDentals.upsert({ name, sso_district: ssoDistrict, in_association: newInAssoc })
      setSaved((xs) => {
        const i = xs.findIndex((x) => x.id === row.id)
        if (i >= 0) { const copy = xs.slice(); copy[i] = row; return copy }
        return [...xs, row].sort((a, b) => a.name.localeCompare(b.name))
      })
      setSelIds((prev) => new Set(prev).add(row.id))
      setNewName(''); setNewAssoc(false); setAdding(false)
    } catch (e) {
      setErr(extractErr(e))
    }
  }

  async function toggleAssoc(bus, ev) {
    ev.stopPropagation()
    try {
      const updated = await api.otherMobileDentals.update(bus.id, { in_association: !bus.in_association })
      setSaved((xs) => xs.map((x) => x.id === bus.id ? updated : x))
    } catch (e) { setErr(extractErr(e)) }
  }
  async function removeBus(bus, ev) {
    ev.stopPropagation()
    try {
      await api.otherMobileDentals.remove(bus.id)
      setSaved((xs) => xs.filter((x) => x.id !== bus.id))
      setSelIds((prev) => { const n = new Set(prev); n.delete(bus.id); return n })
    } catch (e) { setErr(extractErr(e)) }
  }

  async function generate() {
    if (busy) return
    const buses = saved.filter((b) => selectedIds.has(b.id))
      .map((b) => ({ name: b.name, in_association: !!b.in_association }))
    if (!buses.length) { setErr('กรุณาเลือกรถทันตกรรมอย่างน้อย 1 รายการ'); return }
    setErr(null); setBusy(true)
    try {
      await api.operationDocuments.generate(op.id, r.id, { buses })
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        <div className="rounded-lg ring-1 ring-zinc-200 bg-zinc-50 px-3 py-2.5 text-[12px] text-zinc-700">
          <span className="font-medium text-zinc-800">เขตประกันสังคม:</span>{' '}
          {ssoDistrict || <span className="text-rose-600">ยังไม่ระบุใน Operation</span>}
          <span className="block mt-0.5 text-[11px] text-zinc-500">
            ดรอปดาวน์ด้านล่างจะกรองเฉพาะรถทันตกรรมที่ลงทะเบียนในเขตนี้
          </span>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">เลือกรถทันตกรรมที่ต้องส่งหนังสือถึง</span>
            <span className="text-[10.5px] text-zinc-400">{saved.length} รายการ · เลือกแล้ว {selectedIds.size}</span>
          </div>

          <div className="rounded-lg ring-1 ring-zinc-200 bg-white max-h-60 overflow-y-auto">
            {saved.length === 0 && (
              <div className="px-3 py-2.5 text-[12px] text-zinc-500">ยังไม่มีข้อมูลรถทันตกรรมในเขตนี้ — กดเพิ่มด้านล่าง</div>
            )}
            {saved.map((bus) => {
              const picked = selectedIds.has(bus.id)
              return (
                <button key={bus.id} type="button" onClick={() => toggle(bus.id)}
                  className={'w-full text-left px-3 py-2 border-b border-zinc-100 flex items-center justify-between gap-2 ' +
                    (picked ? 'bg-amber-50/60' : 'hover:bg-zinc-50')}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={'w-4 h-4 shrink-0 rounded ring-1 inline-flex items-center justify-center ' +
                      (picked ? 'bg-amber-500 ring-amber-500 text-white' : 'bg-white ring-zinc-300')}>
                      {picked && <Check size={11} />}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-medium text-zinc-800 truncate">{bus.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">
                        {bus.in_association ? 'อยู่ในสมาคมรถทันตกรรม' : 'ไม่อยู่ในสมาคม'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span onClick={(e) => toggleAssoc(bus, e)}
                      className="px-1.5 py-0.5 rounded text-[10.5px] ring-1 ring-zinc-200 hover:bg-zinc-100 text-zinc-600"
                      title="สลับสถานะสมาคม">
                      สมาคม {bus.in_association ? '✓' : '✗'}
                    </span>
                    <span onClick={(e) => removeBus(bus, e)}
                      className="p-1 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600"
                      title="ลบ">
                      <Trash2 size={13} />
                    </span>
                  </div>
                </button>
              )
            })}
          </div>

          {!adding && (
            <button type="button" onClick={() => setAdding(true)}
              className="mt-2 inline-flex items-center gap-1 text-[11.5px] text-amber-700 hover:text-amber-800">
              <Plus size={12} /> เพิ่มรถทันตกรรมใหม่ในเขตนี้
            </button>
          )}
          {adding && (
            <div className="mt-2 rounded-lg ring-1 ring-zinc-200 bg-white p-2.5 space-y-2">
              <GenField label="ชื่อรถทันตกรรม" value={newName} onChange={setNewName} />
              <label className="flex items-center gap-2 text-[12px] text-zinc-700">
                <input type="checkbox" checked={newInAssoc} onChange={(e) => setNewAssoc(e.target.checked)} />
                อยู่ในสมาคมรถทันตกรรม
              </label>
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => { setAdding(false); setNewName(''); setNewAssoc(false) }}
                  className="px-2.5 py-1 rounded text-[12px] text-zinc-600 hover:bg-zinc-100">ยกเลิก</button>
                <button onClick={addBus}
                  className="px-2.5 py-1 rounded text-[12px] bg-amber-500 hover:bg-amber-600 text-white">บันทึก</button>
              </div>
            </div>
          )}
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์รวมหลายหน้าจะถูกสร้างและเก็บใน Wasabi (1 หน้า / 1 รถ)</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy || selectedIds.size === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? `สร้างใหม่ (${selectedIds.size} หน้า)` : `สร้างเอกสาร (${selectedIds.size} หน้า)`}
          </button>
        </div>
      </footer>
    </>
  )
}

// "Check List สสจ ออกหน่วยสุขภาพเคลื่อนที่" — backend uses pdf-lib to fill
// `{{current_date}}` placeholders on the source PDF. Only one field here; the
// rest of the form is pre-filled static text inside the PDF.
function ChecklistSsoForm({ r, op, onClose, onGenerated }) {
  const [currentDate, setCurrentDate] = useState(todayThaiShort())
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const isRegenerating = !!r.generated_url

  async function generate() {
    if (busy) return
    setErr(null); setBusy(true)
    try {
      await api.operationDocuments.generate(op.id, r.id, {
        current_date: currentDate.trim() || undefined,
      })
      onGenerated()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        <div className="rounded-lg ring-1 ring-zinc-200 bg-zinc-50/60 px-3 py-2.5 text-[12px] text-zinc-700 leading-relaxed">
          เอกสารนี้ใช้ PDF ต้นฉบับเดิม ระบบจะใส่เฉพาะ <code className="bg-white px-1 rounded">วันที่</code> ให้อัตโนมัติ
          (สามารถแก้ด้านล่างได้หากต้องการ)
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">วันที่บนเอกสาร</div>
          <GenField label="วันที่ (สั้น)" value={currentDate} onChange={setCurrentDate} />
          <p className="mt-1 text-[10.5px] text-zinc-400">รูปแบบ: 19 ม.ค. 2569 — เว้นว่างเพื่อใช้วันที่วันนี้</p>
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์จะถูกสร้างเป็น PDF บน Wasabi</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

function OnBusAnnouncementForm({ r, op, onClose, onGenerated }) {
  const province = op.location_province || op.company_province || ''

  // Billing payers — auto-populated from company, user can add/remove
  const BILLING_MAP = {
    'ปกส.':        'สำนักงานประกันสังคม',
    'บัตรทอง':    'สำนักงานหลักประกันสุขภาพแห่งชาติ',
    'ประกันกลุ่ม': 'บริษัทประกันเอกชน',
    'สวัสดิการ':  'สวัสดิการบริษัท',
  }
  const [billingPayers, setBillingPayers] = useState(() => {
    try {
      const raw = JSON.parse(op.company_billing_rights || '[]')
      return Array.isArray(raw) ? raw.map((s) => BILLING_MAP[s] || s) : []
    } catch { return [] }
  })
  const [addingPayer, setAddingPayer] = useState(false)
  const [newPayer, setNewPayer]       = useState('')

  function savePayer() {
    const v = newPayer.trim()
    if (!v) return
    setBillingPayers((prev) => [...prev, v])
    setNewPayer(''); setAddingPayer(false)
  }

  // Referral contacts
  const [saved, setSaved]         = useState([])
  const [loadErr, setLoadErr]     = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [adding, setAdding]       = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft]         = useState({ name: '', address: '', phone: '' })
  const [refBusy, setRefBusy]     = useState(false)
  const [refErr, setRefErr]       = useState(null)

  // Generation
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const isRegenerating  = !!r.generated_url

  useEffect(() => {
    fetch(`/api/referral-contacts?province=${encodeURIComponent(province)}`, { credentials: 'include' })
      .then((res) => res.ok ? res.json() : [])
      .then((list) => {
        const arr = Array.isArray(list) ? list : []
        setSaved(arr)
        if (arr.length > 0) setSelectedId(arr[0].id)
      })
      .catch(() => setLoadErr('โหลดข้อมูลไม่สำเร็จ'))
  }, [province])  // eslint-disable-line react-hooks/exhaustive-deps

  async function saveNew() {
    if (!draft.name.trim()) { setRefErr('ชื่อหน่วยส่งต่อจำเป็น'); return }
    setRefBusy(true); setRefErr(null)
    try {
      const res = await fetch('/api/referral-contacts', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, province }),
      })
      if (!res.ok) throw new Error(await res.text())
      const data = await res.json()
      const entry = { id: data.id, ...draft, province }
      setSaved((prev) => [...prev, entry])
      setSelectedId(data.id)
      setAdding(false); setDraft({ name: '', address: '', phone: '' })
    } catch (e) { setRefErr(e.message || 'error') }
    finally { setRefBusy(false) }
  }

  async function saveEdit() {
    if (!draft.name.trim()) { setRefErr('ชื่อหน่วยส่งต่อจำเป็น'); return }
    setRefBusy(true); setRefErr(null)
    try {
      const res = await fetch(`/api/referral-contacts/${editingId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, province }),
      })
      if (!res.ok) throw new Error(await res.text())
      setSaved((prev) => prev.map((s) => s.id === editingId ? { ...s, ...draft } : s))
      setEditingId(null); setDraft({ name: '', address: '', phone: '' })
    } catch (e) { setRefErr(e.message || 'error') }
    finally { setRefBusy(false) }
  }

  async function deleteContact(id, ev) {
    ev.stopPropagation()
    setRefErr(null)
    try {
      const res = await fetch(`/api/referral-contacts/${id}`, { method: 'DELETE', credentials: 'include' })
      if (!res.ok) throw new Error(await res.text())
      const next = saved.filter((s) => s.id !== id)
      setSaved(next)
      if (selectedId === id) setSelectedId(next.length > 0 ? next[0].id : null)
    } catch (e) { setRefErr(e.message || 'error') }
  }

  function startEdit(contact, ev) {
    ev.stopPropagation()
    setEditingId(contact.id)
    setDraft({ name: contact.name, address: contact.address || '', phone: contact.phone || '' })
    setAdding(false); setRefErr(null)
  }

  async function generate() {
    const ref = saved.find((s) => s.id === selectedId)
    if (!ref) { setErr('กรุณาเลือกหน่วยส่งต่อ'); return }
    setBusy(true); setErr(null)
    try {
      await api.operationDocuments.generate(op.id, r.id, {
        billing_payer:    billingPayers,
        referral_name:    ref.name.trim(),
        referral_address: (ref.address || '').trim(),
        referral_phone:   (ref.phone   || '').trim(),
      })
      onGenerated()
    } catch (e) { setErr(extractErr(e)) }
    finally { setBusy(false) }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isRegenerating && (
          <div className="rounded-lg ring-1 ring-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
            กำลังสร้างใหม่ — เอกสารเดิมจะถูกแทนที่ และ Approve จะถูกล้าง
          </div>
        )}

        {/* Province info */}
        <div className="rounded-lg ring-1 ring-zinc-200 bg-zinc-50 px-3 py-2.5 text-[12px] text-zinc-700">
          <span className="font-medium text-zinc-800">จังหวัด:</span>{' '}
          {province || <span className="text-zinc-400 italic">ไม่ระบุ</span>}
          <span className="block mt-0.5 text-[11px] text-zinc-500">
            หน่วยส่งต่อด้านล่างกรองตามจังหวัดนี้
          </span>
        </div>

        {/* Billing payers */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">สิทธิการเบิก</span>
            <span className="text-[10.5px] text-zinc-400">{billingPayers.length} รายการ</span>
          </div>
          <div className="rounded-lg ring-1 ring-zinc-200 bg-white">
            {billingPayers.length === 0 && !addingPayer && (
              <div className="px-3 py-2.5 text-[12px] text-zinc-400 italic">ไม่มีสิทธิ — จะแสดงว่างในเอกสาร</div>
            )}
            {billingPayers.map((p, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 last:border-0">
                <span className="flex-1 text-[12.5px] text-zinc-800 truncate">{p}</span>
                <button onClick={() => setBillingPayers((prev) => prev.filter((_, j) => j !== i))}
                  className="p-1 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600 shrink-0">
                  <X size={11} />
                </button>
              </div>
            ))}
            {addingPayer && (
              <div className="flex items-center gap-2 px-3 py-2 border-t border-zinc-100">
                <input autoFocus value={newPayer} onChange={(e) => setNewPayer(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') savePayer() }}
                  placeholder="ชื่อสิทธิ เช่น สำนักงานประกันสังคม…"
                  className="flex-1 px-2 py-1 text-[12.5px] ring-1 ring-amber-300 rounded focus:outline-none" />
                <button onClick={savePayer}
                  className="px-2 py-1 rounded text-[12px] bg-amber-500 hover:bg-amber-600 text-white">เพิ่ม</button>
                <button onClick={() => { setAddingPayer(false); setNewPayer('') }}
                  className="px-2 py-1 rounded text-[12px] text-zinc-500 hover:bg-zinc-100">ยกเลิก</button>
              </div>
            )}
          </div>
          {!addingPayer && (
            <button type="button" onClick={() => setAddingPayer(true)}
              className="mt-2 inline-flex items-center gap-1 text-[11.5px] text-amber-700 hover:text-amber-800">
              <Plus size={12} /> เพิ่มสิทธิ
            </button>
          )}
        </div>

        {/* Referral contacts */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">หน่วยส่งต่อ</span>
            <span className="text-[10.5px] text-zinc-400">
              {saved.length} รายการ{selectedId ? ' · เลือกแล้ว 1' : ''}
            </span>
          </div>

          <div className="rounded-lg ring-1 ring-zinc-200 bg-white max-h-60 overflow-y-auto">
            {loadErr && (
              <div className="px-3 py-2.5 text-[12px] text-rose-600">{loadErr}</div>
            )}
            {!loadErr && saved.length === 0 && (
              <div className="px-3 py-2.5 text-[12px] text-zinc-500">
                ยังไม่มีหน่วยส่งต่อสำหรับจังหวัดนี้ — กดเพิ่มด้านล่าง
              </div>
            )}
            {saved.map((contact) => {
              const isSelected = selectedId === contact.id
              const isEditing  = editingId  === contact.id
              return (
                <div key={contact.id}>
                  <button type="button" onClick={() => { if (!isEditing) setSelectedId(contact.id) }}
                    className={'w-full text-left px-3 py-2 border-b border-zinc-100 flex items-center justify-between gap-2 ' +
                      (isSelected ? 'bg-amber-50/60' : 'hover:bg-zinc-50')}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={'w-4 h-4 shrink-0 rounded ring-1 inline-flex items-center justify-center ' +
                        (isSelected ? 'bg-amber-500 ring-amber-500 text-white' : 'bg-white ring-zinc-300')}>
                        {isSelected && <Check size={11} />}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-medium text-zinc-800 truncate">{contact.name}</div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {contact.phone || contact.address || '—'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span onClick={(e) => startEdit(contact, e)}
                        className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
                        title="แก้ไข">
                        <Pencil size={13} />
                      </span>
                      <span onClick={(e) => deleteContact(contact.id, e)}
                        className="p-1 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600"
                        title="ลบ">
                        <Trash2 size={13} />
                      </span>
                    </div>
                  </button>
                  {isEditing && (
                    <div className="px-3 py-2.5 bg-zinc-50/80 border-b border-zinc-100 space-y-2">
                      <GenField label="ชื่อ" value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} />
                      <GenField label="ที่อยู่" value={draft.address} onChange={(v) => setDraft((d) => ({ ...d, address: v }))} textarea />
                      <GenField label="เบอร์โทร" value={draft.phone} onChange={(v) => setDraft((d) => ({ ...d, phone: v }))} />
                      {refErr && <div className="text-[11.5px] text-rose-600">{refErr}</div>}
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => { setEditingId(null); setDraft({ name: '', address: '', phone: '' }); setRefErr(null) }}
                          className="px-2.5 py-1 rounded text-[12px] text-zinc-600 hover:bg-zinc-100">ยกเลิก</button>
                        <button onClick={saveEdit} disabled={refBusy}
                          className="px-2.5 py-1 rounded text-[12px] bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60">บันทึก</button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {!adding && !editingId && (
            <button type="button"
              onClick={() => { setAdding(true); setDraft({ name: '', address: '', phone: '' }); setRefErr(null) }}
              className="mt-2 inline-flex items-center gap-1 text-[11.5px] text-amber-700 hover:text-amber-800">
              <Plus size={12} /> เพิ่มหน่วยส่งต่อใหม่{province ? `ในจังหวัดนี้` : ''}
            </button>
          )}
          {adding && (
            <div className="mt-2 rounded-lg ring-1 ring-zinc-200 bg-white p-2.5 space-y-2">
              <GenField label="ชื่อหน่วยส่งต่อ" value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} />
              <GenField label="ที่อยู่" value={draft.address} onChange={(v) => setDraft((d) => ({ ...d, address: v }))} textarea />
              <GenField label="เบอร์โทร" value={draft.phone} onChange={(v) => setDraft((d) => ({ ...d, phone: v }))} />
              {refErr && <div className="text-[11.5px] text-rose-600">{refErr}</div>}
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => { setAdding(false); setDraft({ name: '', address: '', phone: '' }); setRefErr(null) }}
                  className="px-2.5 py-1 rounded text-[12px] text-zinc-600 hover:bg-zinc-100">ยกเลิก</button>
                <button onClick={saveNew} disabled={refBusy}
                  className="px-2.5 py-1 rounded text-[12px] bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60">บันทึก</button>
              </div>
            </div>
          )}
        </div>

        {err && (
          <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}
      </div>

      <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-400">ไฟล์ PDF สร้างจาก Google Docs → เก็บใน Wasabi</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={generate} disabled={busy || !selectedId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isRegenerating ? 'สร้างใหม่' : 'สร้างเอกสาร'}
          </button>
        </div>
      </footer>
    </>
  )
}

function todayThaiShort() {
  // "19 ม.ค. 2569" — Asia/Bangkok, Arabic digits.
  return new Intl.DateTimeFormat('th-TH-u-ca-gregory', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok',
  }).format(new Date())
}

// Same logic as backend formalSsoName() — kept client-side just for the
// form's default-value preview. Backend always re-derives on submit.
function formalSsoName(province, ssoDistrict) {
  const p = (province     || '').trim()
  const d = (ssoDistrict  || '').trim()
  if (!p) return d
  if (p === 'กรุงเทพมหานคร') {
    const m = d.match(/พื้นที่\s*(\d+)/)
    return m
      ? `สำนักงานประกันสังคมจังหวัดกรุงเทพมหานคร พื้นที่ ${m[1]}`
      : 'สำนักงานประกันสังคมจังหวัดกรุงเทพมหานคร'
  }
  const base = `สำนักงานประกันสังคมจังหวัด${p}`
  const branchMatch = d.match(/(สาขา\S+)/)
  return branchMatch ? `${base} ${branchMatch[1]}` : base
}

function GenField({ label, value, onChange, textarea, tabular, addrSrc }) {
  const cls = 'block w-full px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[13px] ' +
    (tabular ? 'tabular-nums ' : '') + (textarea ? 'resize-y' : '')
  return (
    <label className="block">
      <span className={addrSrc ? 'flex items-center justify-between gap-2 mb-1' : 'block mb-1'}>
        <span className="text-[11.5px] text-zinc-600 font-medium">{label}</span>
        {addrSrc && <AddrSourceChips value={value} onChange={onChange} company={addrSrc.company} operation={addrSrc.operation} />}
      </span>
      {textarea
        ? <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
        : <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className={cls} />}
    </label>
  )
}

// Two-source picker for an address field: fills the textarea from either the
// company's registered address (company database) or the operation's on-site
// address. Staff can still type freely; the active chip just reflects a match.
function AddrSourceChips({ value, onChange, company, operation }) {
  const c = (company || '').trim(), o = (operation || '').trim(), cur = (value || '').trim()
  const chip = (active) =>
    'px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 transition-colors ' +
    (active ? 'bg-amber-500 text-white ring-amber-500' : 'bg-white text-zinc-500 ring-zinc-200 hover:bg-zinc-50')
  return (
    <span className="inline-flex gap-1 shrink-0">
      {c && (
        <button type="button" title="ใช้ที่อยู่บริษัท (ฐานข้อมูล)" onClick={() => onChange(company)} className={chip(cur === c)}>
          บริษัท
        </button>
      )}
      {o && (
        <button type="button" title="ใช้ที่อยู่หน้างาน (Operation)" onClick={() => onChange(operation)} className={chip(cur === o)}>
          หน้างาน
        </button>
      )}
    </span>
  )
}

function ceYearOf(dateStr) {
  if (!dateStr) return 0
  return parseInt(dateStr.slice(0, 4), 10) || 0
}
const TH_MONTHS_SHORT = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
const TH_MONTHS_FULL = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน','กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม']
function fmtThaiBeDate(dateStr) {
  const [y, m, d] = (dateStr || '').split('-').map(Number)
  if (!y || !m || !d) return dateStr || '—'
  return `${d} ${TH_MONTHS_SHORT[m - 1]} ${y + 543}`
}
// Long-form Thai date with full month name — e.g. '1 กันยายน 2569'. Used by
// PHO_SPO4's {{date}}, which always renders as a range "<long> จนถึง <long>";
// single-day ops repeat the same date on both sides.
function fmtThaiBeDateLong(dateStr) {
  const [y, m, d] = (dateStr || '').split('-').map(Number)
  if (!y || !m || !d) return ''
  return `${d} ${TH_MONTHS_FULL[m - 1]} ${y + 543}`
}
function fmtThaiBeDateLongRange(start, end) {
  const s = fmtThaiBeDateLong(start)
  const e = fmtThaiBeDateLong(end) || s
  return s ? `${s} จนถึง ${e}` : ''
}


/* ──────────────────────────────── helpers ──────────────────────────────── */

function KindIcon({ kindSlug, size = 18 }) {
  if (kindSlug === 'authorized-signer-id') return <FileImage size={size} className="text-amber-500 shrink-0 mt-0.5" />
  return <FileText size={size} className="text-amber-500 shrink-0 mt-0.5" />
}
function FileTypeIcon({ contentType }) {
  if (contentType === 'application/pdf') return <FileType size={14} className="text-rose-500 shrink-0" />
  if (contentType?.startsWith('image/')) return <FileImage size={14} className="text-emerald-500 shrink-0" />
  return <FileText size={14} className="text-zinc-400 shrink-0" />
}
function displayLabel(r) { return r.custom_label || r.kind_name }
function fmtDate(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short' }) + ' · ' +
    d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}
function slug(s) {
  return String(s || 'doc').replace(/[^\w\- ก-๙]/g, '').trim().replace(/\s+/g, '-').slice(0, 60) || 'doc'
}
function extractErr(e) {
  const m = (e?.message || '').match(/:\s*\{?"?error"?"?:\s*"?([^"}\n]+)/)
  return m ? m[1] : (e?.message || 'error')
}
function fileToDataUri(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload  = () => resolve(String(r.result || ''))
    r.onerror = () => reject(new Error('read failed'))
    r.readAsDataURL(file)
  })
}

function LoadingCard() {
  return <div className={`${CARD} px-5 py-10 text-center text-zinc-400`}>
    <Loader2 className="inline w-4 h-4 animate-spin mr-1.5" /> Loading…
  </div>
}
function ErrorCard({ message }) {
  return <div className={`${CARD} px-5 py-6 text-rose-700 bg-rose-50 ring-rose-200`}>{message}</div>
}
